import { useState, useRef, useEffect, useCallback, useMemo, memo, lazy, Suspense, Fragment } from "react";
import { createPortal } from "react-dom";
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import Icon1 from "@assets/icons/Icon";
import { UserAvatar } from "@components/ui/UserAvatar";
import ClientAvatar from "@components/chat/ClientAvatar";

import { useMessagerieStore } from "@store/messagerieStore";
import { useChatStageStore } from "@store/chatStageStore";
import { useOrdreStore } from "@store/ordreStore";
import { useDossierStore } from "@store/dossierStore";
import { useDataStore } from "@store/user_org";
import { messagerieApi } from "@services/api/messagerie";
import { useClientMute, muteTooltip } from "./useClientMute";
import { ordresApi } from "@services/api/ordres";
import { dossiersApi } from "@services/api/dossiers";
import { useIsDevOrg } from "@helpers/accessHelpers";
import { matchesNamePrefix } from "@utils/textSearch";
import { whatsappApi } from "@services/api/organizations";
import type { WhatsAppTemplate } from "@services/api/organizations";
import { useWhatsAppStatusStore, whatsAppBlock } from "@store/whatsappStatusStore";
import type { WhatsAppBlock } from "@store/whatsappStatusStore";
import { showNotification } from "context/notification.service";
import firebase from "@services/firebase";
import { collection, query, orderBy, limit as fsLimit, onSnapshot } from "firebase/firestore";
import ForwardModal from "./ForwardModal";
import WhatsAppTemplateModal from "./WhatsAppTemplateModal";
import type { ChatMessage as StoreChatMessage, DiscussionTab, CallEvent, ChatAttachment } from "types/messaging";
import {
  getAttachments,
  isImageAtt,
  isAudioAtt,
  isPdfAtt,
  isVideoAtt,
  ImageAlbum,
  PdfCard,
  VideoCard,
  DaySeparator,
  msgDayKey,
  linkify,
  PinGlyph,
  PinnedBar,
  renderTextWithFolderTokens,
  useFolderSearch,
  FolderSearchPopover,
  navigateToFolderRef,
  staffSafeHref,
  type FolderRef,
} from "./messageParts";

const AudioPlayer = lazy(() => import("./AudioPlayer"));

/** Force a real download (vs. opening the URL in a new tab) by fetching the
 * blob and triggering an <a download> click. Falls back to window.open on
 * CORS or fetch errors. */
async function downloadFile(url: string, name?: string): Promise<void> {
  if (!url) return;
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = name || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, "_blank");
  }
}

// ─── Inline Info Panel (used when onToggleInfo is not provided) ──

type InfoTab = "medias" | "fichiers" | "liens";

function ChatInfoPanel({
  onClose,
  discussionName,
  discussionDescription,
  chatMembers,
  messages,
}: {
  onClose: () => void;
  discussionName: string;
  discussionDescription?: string;
  chatMembers: ChatMember[];
  messages: DisplayMessage[];
}) {
  const [activeTab, setActiveTab] = useState<InfoTab>("medias");
  const [previewImage, setPreviewImage] = useState<DisplayMessage | null>(null);

  const handleDownload = async (url?: string, base64?: string, name?: string) => {
    const href = base64 || url;
    if (!href) return;
    await downloadFile(href, name);
  };

  const medias = messages.filter((m) => m.attachment?.type === "image");
  const files = messages.filter((m) => m.attachment?.type === "file");

  const urlRegex = /https?:\/\/[^\s]+/g;
  const links: { url: string; messageId: string }[] = [];
  messages.forEach((m) => {
    if (m.text) {
      const matches = m.text.match(urlRegex);
      if (matches) matches.forEach((url) => links.push({ url, messageId: m.id }));
    }
  });

  const tabs: { id: InfoTab; label: string; count: number }[] = [
    { id: "medias", label: "Medias", count: medias.length },
    { id: "fichiers", label: "Fichiers", count: files.length },
    { id: "liens", label: "Liens", count: links.length },
  ];

  return (
    <div
      className="flex flex-col flex-shrink-0"
      style={{ width: 300, height: "100%", background: "#FFFFFF", borderLeft: "1px solid #EEEEEE", overflowY: "auto" }}
    >
      {/* Header */}
      <div className="flex flex-row items-center justify-between flex-shrink-0" style={{ padding: "20px 20px 0" }}>
        <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 16, color: "#000000" }}>Infos</span>
        <button onClick={onClose} className="hover:bg-neutral-100 rounded-full flex items-center justify-center" style={{ width: 28, height: 28 }}>
          <Icon1 name="close" size={12} color="#A1A1A1" />
        </button>
      </div>

      {/* Conv name */}
      <div className="flex flex-col flex-shrink-0" style={{ padding: "16px 20px", gap: 12 }}>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 18, color: "#000000" }}>{discussionName}</span>
          {discussionDescription && (
            <span style={{ fontFamily: "Poppins", fontWeight: 400, fontSize: 13, color: "#888888" }}>{discussionDescription}</span>
          )}
        </div>
      </div>

      {/* Members */}
      <div className="flex flex-col flex-shrink-0" style={{ padding: "0 20px 16px", gap: 8, borderBottom: "1px solid #F0F0F0" }}>
        <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#A1A1A1" }}>Membres ({chatMembers.length})</span>
        <div className="flex flex-col" style={{ gap: 2 }}>
          {chatMembers.map((m, i) => (
            <div key={m.name + i} className="flex flex-row items-center" style={{ gap: 10, padding: "5px 0" }}>
              <UserAvatar src={m.photo_url} value={m.avatar_seed || m.name} size={30} className="rounded-full flex-shrink-0" />
              <div className="flex flex-col flex-1" style={{ gap: 0 }}>
                <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#000000" }}>{m.name}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-row flex-shrink-0" style={{ padding: "12px 20px 0", gap: 0 }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className="flex-1 flex items-center justify-center"
            style={{
              fontFamily: "Poppins", fontWeight: activeTab === t.id ? 600 : 500, fontSize: 13,
              color: activeTab === t.id ? "#000000" : "#AAAAAA", padding: "8px 0", background: "transparent",
              borderTop: "none", borderLeft: "none", borderRight: "none",
              borderBottomWidth: 2, borderBottomStyle: "solid", borderBottomColor: activeTab === t.id ? "#6366f1" : "transparent",
            }}
          >
            {t.label} {t.count > 0 && <span style={{ marginLeft: 4, color: "#AAAAAA", fontWeight: 400 }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1" style={{ padding: "12px 20px", overflowY: "auto", minHeight: 0 }}>
        {activeTab === "medias" && (
          medias.length === 0 ? (
            <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#CCCCCC", display: "block", textAlign: "center", marginTop: 30 }}>Aucun media</span>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
              {medias.map((m) => (
                <div
                  key={m.id}
                  className="group relative cursor-pointer"
                  style={{ aspectRatio: "1", borderRadius: 6, overflow: "hidden", background: "#F5F5F5" }}
                  onClick={() => setPreviewImage(m)}
                >
                  <img src={m.attachment!.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "rgba(0,0,0,0.35)" }}>
                    <Icon1 name="eye" size={20} color="#FFFFFF" />
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {activeTab === "fichiers" && (
          files.length === 0 ? (
            <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#CCCCCC", display: "block", textAlign: "center", marginTop: 30 }}>Aucun fichier</span>
          ) : (
            <div className="flex flex-col" style={{ gap: 4 }}>
              {files.map((m) => (
                <div
                  key={m.id}
                  className="flex flex-row items-center hover:bg-neutral-50 cursor-pointer"
                  style={{ padding: "8px 10px", gap: 10, borderRadius: 8 }}
                  onClick={() => handleDownload(m.attachment!.url, undefined, m.attachment!.name)}
                >
                  <Icon1 name="file" size={18} color="#6366f1" />
                  <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#000000", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.attachment!.name || "Fichier"}
                  </span>
                  <Icon1 name="download" size={14} color="#BBBBBB" />
                </div>
              ))}
            </div>
          )
        )}

        {activeTab === "liens" && (
          links.length === 0 ? (
            <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#CCCCCC", display: "block", textAlign: "center", marginTop: 30 }}>Aucun lien</span>
          ) : (
            <div className="flex flex-col" style={{ gap: 4 }}>
              {links.map((l, i) => (
                <a
                  key={i}
                  href={staffSafeHref(l.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-row items-center hover:bg-neutral-50"
                  style={{ padding: "8px 10px", gap: 10, borderRadius: 8, textDecoration: "none" }}
                >
                  <Icon1 name="linkedFile" size={18} color="#6366f1" />
                  <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#6366f1", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {l.url}
                  </span>
                </a>
              ))}
            </div>
          )
        )}
      </div>

      {previewImage && previewImage.attachment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setPreviewImage(null)}>
          <img src={previewImage.attachment.url} alt="" style={{ maxWidth: "80vw", maxHeight: "80vh", borderRadius: 8 }} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

/** WhatsApp-style typing dots animation */
function TypingDots() {
  return (
    <span className="inline-flex items-center" style={{ gap: 3 }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="inline-block rounded-full"
          style={{
            width: 6,
            height: 6,
            background: "#A1A1A1",
            animation: `waTyping 1.2s ease-in-out ${i * 0.15}s infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes waTyping {
          0%   { opacity: 0.3; transform: translateY(0); }
          25%  { opacity: 1;   transform: translateY(-3px); }
          50%  { opacity: 0.3; transform: translateY(0); }
          100% { opacity: 0.3; transform: translateY(0); }
        }
      `}</style>
    </span>
  );
}

// ─── Types ──────────────────────────────────────────────────

const EMOJI_LIST = ["👍", "❤️", "😂", "🔥", "😍", "👏", "😮", "😢", "🙏", "💯", "✅", "🎉"];
const EMPTY_READ_RECEIPTS: import("types/messaging").ReadReceipt[] = [];

type SenderType = "bot" | "client" | "user";

/** Display-ready message used internally by ChatPanel */
interface DisplayMessage {
  id: string;
  senderName: string;
  senderRole: string;
  senderIdRaw: string;
  senderType: SenderType;
  senderPhotoUrl?: string;
  senderAvatarSeed?: string;
  text?: string;
  timestamp: string;
  createdAt?: string;
  isOwn: boolean;
  attachment?: { name: string; type: "pdf" | "image" | "file"; url?: string; mime_type?: string; gcs_path?: string };
  /** Full attachment list (multi-file messages). Falls back to [attachment]. */
  attachments?: ChatAttachment[];
  reactions: { emoji: string; userId: string }[];
  replyToId?: string;
  folderRefs?: { id: string; name: string }[];
  deleted?: boolean;
  edited?: boolean;
  channel?: string | null;
  mentions?: string[];
  wa_status?: import("types/messaging").WaDeliveryStatus;
  wa_error?: string | null;
  wa_failed_reason?: string | null;
  wa_retry_pending?: boolean | null;
  email_status?: import("types/messaging").EmailDeliveryStatus;
  event?: CallEvent;
}

export interface ChatMember {
  name: string;
  photo_url?: string;
  avatar_seed?: string;
}

// ─── Adapter ────────────────────────────────────────────────

const BOT_AVATAR_SEED = "__hexaflow_bot__";
const CLIENT_AVATAR_SEED = "__hexaflow_client__";

function classifySender(senderId: string): SenderType {
  if (senderId === "system") return "bot";
  if (senderId.startsWith("wa_") || senderId.startsWith("email_")) return "client";
  return "user";
}

function toDisplayMessage(msg: StoreChatMessage, currentUserId: string, members: ReturnType<typeof useMessagerieStore.getState>["members"]): DisplayMessage {
  const senderId = msg.sender_id || msg.senderId || "";
  const senderType = classifySender(senderId);
  const member = senderType === "user" ? members.find((m) => m.id === senderId) : undefined;

  let senderName: string;
  let senderPhotoUrl: string | undefined;
  let senderAvatarSeed: string | undefined;

  if (senderType === "bot") {
    senderName = "Bot";
    senderAvatarSeed = BOT_AVATAR_SEED;
  } else if (senderType === "client") {
    senderName = msg.sender_name || "Client";
    senderAvatarSeed = CLIENT_AVATAR_SEED;
  } else {
    senderName = member?.name || msg.sender_name || "Inconnu";
    senderPhotoUrl = member?.photo_url;
    senderAvatarSeed = member?.avatar_seed;
  }

  return {
    id: msg.id,
    senderName,
    senderRole: senderType === "user" ? (member?.role || "") : "",
    senderIdRaw: senderId,
    senderType,
    senderPhotoUrl,
    senderAvatarSeed,
    text: msg.text,
    timestamp: msg.timestamp || "",
    isOwn: senderId === currentUserId,
    attachment: msg.attachment ? { name: msg.attachment.name || "Fichier", type: msg.attachment.type === "image" ? "image" : "file", url: msg.attachment.url, mime_type: msg.attachment.mime_type || msg.attachment.mimeType, gcs_path: msg.attachment.gcs_path } : undefined,
    attachments: getAttachments(msg),
    reactions: (msg.reactions || []).map((r) => ({ emoji: r.emoji, userId: r.user_id || r.userId || "" })),
    replyToId: msg.reply_to_id || msg.replyToId,
    folderRefs: msg.folder_refs,
    deleted: msg.deleted,
    edited: msg.edited,
    createdAt: msg.created_at,
    channel: msg.channel,
    mentions: msg.mentions,
    wa_status: msg.wa_status,
    wa_error: msg.wa_error,
    wa_failed_reason: msg.wa_failed_reason,
    wa_retry_pending: msg.wa_retry_pending,
    email_status: msg.email_status,
    event: msg.event,
  };
}

function formatCallDuration(seconds?: number | null): string {
  if (!seconds || seconds <= 0) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}min${s > 0 ? ` ${s}s` : ""}`;
}

function CallEventCard({ event, isOwn, isDark }: { event: CallEvent; isOwn: boolean; isDark: boolean }) {
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const incoming = event.direction === "in";
  const missed = event.status === "missed";
  const titleColor = missed ? "#DC2626" : (isDark || isOwn ? "#FFFFFF" : "#1E1E1E");
  const subColor = isDark || isOwn ? "rgba(255,255,255,0.7)" : "#737373";
  const direction_label = incoming ? "Appel reçu" : "Appel passé";
  const status_label = missed ? "Manqué" : "Décroché";
  const duration = formatCallDuration(event.duration_sec);

  return (
    <div style={{ padding: "10px 12px 8px", minWidth: 240, maxWidth: 360 }}>
      <div className="flex flex-row items-center" style={{ gap: 8, marginBottom: 4 }}>
        <Icon1 name="phone" size={14} color={titleColor} />
        <span className="text-h3 font-semibold" style={{ fontFamily: "Poppins", color: titleColor }}>
          {direction_label}
        </span>
        <span className="text-h5" style={{ fontFamily: "Poppins", color: subColor }}>
          · {status_label}
          {duration && ` · ${duration}`}
        </span>
      </div>
      <div className="text-h4" style={{ fontFamily: "Poppins", color: subColor, lineHeight: "18px" }}>
        {event.member_name && <div>Membre : <span style={{ color: titleColor, fontWeight: 500 }}>{event.member_name}</span></div>}
        {event.caller_phone && <div>Numéro : {event.caller_phone}</div>}
      </div>
      {event.summary && (
        <div className="text-h4" style={{ fontFamily: "Poppins", color: titleColor, marginTop: 6, fontStyle: "italic", lineHeight: "18px" }}>
          {event.summary}
        </div>
      )}
      {event.transcript && (
        <div style={{ marginTop: 6 }}>
          <button
            onClick={() => setTranscriptOpen((v) => !v)}
            className="text-h5 hover:underline"
            style={{ fontFamily: "Poppins", fontWeight: 500, color: subColor }}
          >
            {transcriptOpen ? "Masquer la transcription" : "Voir la transcription"}
          </button>
          {transcriptOpen && (
            <div
              className="text-h5"
              style={{
                fontFamily: "Poppins",
                color: titleColor,
                marginTop: 6,
                padding: "8px 10px",
                background: isDark || isOwn ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
                borderRadius: 6,
                whiteSpace: "pre-wrap",
                lineHeight: "18px",
                maxHeight: 240,
                overflowY: "auto",
              }}
            >
              {event.transcript}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────

function groupChatMessages(messages: DisplayMessage[]) {
  const groups: { senderId: string; senderIdRaw: string; senderType: SenderType; isOwn: boolean; photoUrl?: string; avatarSeed?: string; messages: DisplayMessage[] }[] = [];
  for (const msg of messages) {
    if (msg.deleted) {
      groups.push({ senderId: msg.senderName, senderIdRaw: msg.senderIdRaw, senderType: msg.senderType, isOwn: msg.isOwn, photoUrl: msg.senderPhotoUrl, avatarSeed: msg.senderAvatarSeed, messages: [msg] });
      continue;
    }
    const last = groups[groups.length - 1];
    const dayChanged = last && msgDayKey({ created_at: last.messages[0].createdAt }) !== msgDayKey({ created_at: msg.createdAt });
    if (last && !dayChanged && last.isOwn === msg.isOwn && last.senderIdRaw === msg.senderIdRaw && !last.messages[last.messages.length - 1].deleted) {
      last.messages.push(msg);
    } else {
      groups.push({ senderId: msg.senderName, senderIdRaw: msg.senderIdRaw, senderType: msg.senderType, isOwn: msg.isOwn, photoUrl: msg.senderPhotoUrl, avatarSeed: msg.senderAvatarSeed, messages: [msg] });
    }
  }
  return groups;
}

function getBubbleRadius(isOwn: boolean, position: "solo" | "first" | "middle" | "last"): string {
  if (position === "solo") return isOwn ? "10px 10px 4px 10px" : "10px 10px 10px 4px";
  if (isOwn) {
    if (position === "first") return "10px 10px 4px 10px";
    if (position === "middle") return "10px 4px 4px 10px";
    return "10px 4px 10px 10px";
  }
  if (position === "first") return "10px 10px 10px 4px";
  if (position === "middle") return "4px 10px 10px 4px";
  return "4px 10px 10px 10px";
}

// ─── Sub-components ─────────────────────────────────────────

function ChatEmojiPicker({
  onSelect,
  onClose,
  alignRight,
}: {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  alignRight?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute z-50"
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        bottom: "100%",
        ...(alignRight ? { right: 0 } : { left: 0 }),
        marginBottom: 4,
        padding: 6,
        background: "#FFFFFF",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        borderRadius: 10,
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gap: 2,
      }}
    >
      {EMOJI_LIST.map((emoji) => (
        <button
          key={emoji}
          onClick={() => onSelect(emoji)}
          className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
          style={{ width: 32, height: 32, fontSize: 18 }}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

function ReactionPill({
  emoji,
  count,
  isMine,
  onToggle,
}: {
  emoji: string;
  count: number;
  isMine: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="flex items-center justify-center"
        style={{ height: 20, padding: 0, gap: 1, background: "transparent", border: "none" }}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>{emoji}</span>
        {count > 1 && (
          <span
            className="text-h5"
            style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: "#999999" }}
          >
            {count}
          </span>
        )}
      </button>
    </div>
  );
}

// ─── Read ticks ─────────────────────────────────────────────

function ReadTicks({ status, isDark }: { status: "sent" | "read_some" | "read_all" | "delivered" | "read" | "failed"; isDark?: boolean }) {
  if (status === "failed") {
    return (
      <svg width="12" height="9" viewBox="0 0 12 9" fill="none" style={{ flexShrink: 0 }}>
        <path d="M2 1.5L10 7.5M10 1.5L2 7.5" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }
  if (status === "sent") {
    return (
      <svg width="12" height="9" viewBox="0 0 12 9" fill="none" style={{ flexShrink: 0 }}>
        <path d="M1 4.5L4 7.5L10.5 1" stroke={isDark ? "#A3A3A3" : "#A3A3A3"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  const color = (status === "read_all" || status === "read") ? "#34B7F1" : (isDark ? "#A3A3A3" : "#A3A3A3");
  return (
    <svg width="17" height="9" viewBox="0 0 17 9" fill="none" style={{ flexShrink: 0 }}>
      <path d="M1 4.5L4 7.5L10.5 1" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.5 4.5L8.5 7.5L15 1" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Read info panel ─────────────────────────────────────────

function ReadInfoPanel({
  msgIndex,
  messages,
  storeMessages,
  readReceipts,
  allMemberIds,
  storeMembers,
  currentUserId,
  isDark,
  onClose,
}: {
  msgIndex: number;
  messages: { id: string }[];
  storeMessages: import("types/messaging").ChatMessage[];
  readReceipts: import("types/messaging").ReadReceipt[];
  allMemberIds: string[];
  storeMembers: { id: string; name: string; photo_url?: string; avatar_seed?: string }[];
  currentUserId: string;
  isDark?: boolean;
  onClose: () => void;
}) {
  const otherMemberIds = allMemberIds.filter((id) => id !== currentUserId);
  const getReadTimeForMessage = useMessagerieStore((s) => s.getReadTimeForMessage);
  const targetMessageId = msgIndex >= 0 ? messages[msgIndex]?.id : undefined;

  const { readers, notRead } = useMemo(() => {
    const readers: { name: string; time: string; photoUrl?: string; avatarSeed?: string }[] = [];
    const notRead: { name: string; photoUrl?: string; avatarSeed?: string }[] = [];
    const targetMessage = targetMessageId ? storeMessages.find((m) => m.id === targetMessageId) : undefined;
    for (const memberId of otherMemberIds) {
      const member = storeMembers.find((m) => m.id === memberId);
      const memberName = member?.name || "Inconnu";
      const photoUrl = member?.photo_url;
      const avatarSeed = member?.avatar_seed;
      const receipt = readReceipts.find((r) => r.user_id === memberId);
      if (receipt) {
        const receiptIndex = messages.findIndex((m) => m.id === receipt.last_read_message_id);
        if (receiptIndex >= msgIndex) {
          // Checkpoint-accurate read time: when this reader actually read up to
          // (or past) this specific message.
          const readAt = targetMessage
            ? getReadTimeForMessage(receipt, targetMessage, storeMessages)
            : receipt.last_read_at;
          const d = new Date(readAt || receipt.last_read_at);
          const time = isNaN(d.getTime()) ? "" : d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
          readers.push({ name: memberName, time, photoUrl, avatarSeed });
        } else {
          notRead.push({ name: memberName, photoUrl, avatarSeed });
        }
      } else {
        notRead.push({ name: memberName, photoUrl, avatarSeed });
      }
    }
    return { readers, notRead };
  }, [msgIndex, messages, storeMessages, readReceipts, otherMemberIds, storeMembers, targetMessageId, getReadTimeForMessage]);

  const bg = isDark ? "#2D2D2D" : "#FFFFFF";
  const border = isDark ? "#404040" : "#E5E5E5";
  const textPrimary = isDark ? "#F5F5F5" : "#1E1E1E";
  const textSecondary = isDark ? "#A1A1A1" : "#6B7280";

  return createPortal(
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.35)" }}
    >
      <div
        className="flex flex-col rounded-2xl"
        style={{ background: bg, border: `1px solid ${border}`, maxHeight: "60vh", overflow: "hidden", width: 320 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-row items-center justify-between" style={{ padding: "14px 16px 10px", borderBottom: `1px solid ${border}` }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: textPrimary }}>
            Infos du message
          </span>
          <button onClick={onClose} className="flex items-center justify-center" style={{ width: 24, height: 24, borderRadius: 6 }}>
            <Icon1 name="close" size={14} color={textSecondary} />
          </button>
        </div>

        <div className="overflow-y-auto" style={{ padding: "10px 0 16px" }}>
          {/* Read by */}
          {readers.length > 0 && (
            <div>
              <div className="flex flex-row items-center" style={{ padding: "4px 16px 6px", gap: 6 }}>
                <svg width="15" height="10" viewBox="0 0 17 9" fill="none">
                  <path d="M1 4.5L4 7.5L10.5 1" stroke="#34B7F1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5.5 4.5L8.5 7.5L15 1" stroke="#34B7F1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 11, color: "#34B7F1", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Lu par {readers.length}
                </span>
              </div>
              {readers.map((r, i) => (
                <div key={i} className="flex flex-row items-center justify-between" style={{ padding: "6px 16px" }}>
                  <div className="flex flex-row items-center" style={{ gap: 10 }}>
                    <UserAvatar src={r.photoUrl} value={r.avatarSeed || r.name} size={32} className="w-8 h-8 rounded-full" />
                    <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: textPrimary }}>{r.name}</span>
                  </div>
                  {r.time && (
                    <span style={{ fontFamily: "Poppins", fontSize: 11, color: textSecondary }}>{r.time}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Not read */}
          {notRead.length > 0 && (
            <div style={{ marginTop: readers.length > 0 ? 8 : 0 }}>
              <div className="flex flex-row items-center" style={{ padding: "4px 16px 6px", gap: 6 }}>
                <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                  <path d="M1 4.5L4 7.5L10.5 1" stroke={textSecondary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 11, color: textSecondary, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Pas encore lu {notRead.length}
                </span>
              </div>
              {notRead.map((r, i) => (
                <div key={i} className="flex flex-row items-center" style={{ padding: "6px 16px", gap: 10 }}>
                  <UserAvatar src={r.photoUrl} value={r.avatarSeed || r.name} size={32} className="w-8 h-8 rounded-full" />
                  <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: textSecondary }}>{r.name}</span>
                </div>
              ))}
            </div>
          )}

          {otherMemberIds.length === 0 && (
            <div className="flex items-center justify-center py-6">
              <span style={{ fontFamily: "Poppins", fontSize: 13, color: textSecondary }}>Conversation privée</span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

const MENTION_REGEX = /(@[\p{L}\p{N}\s]+?)(?=\s@|\s*$|[.,;!?)])/gu;

function Linkify({ text, isOwn, isDark, mentionIds, folderRefs }: { text: string; isOwn?: boolean; isDark?: boolean; mentionIds?: string[]; folderRefs?: { id: string; name: string }[]; currentUserId?: string }) {
  const hasMentions = mentionIds && mentionIds.length > 0;
  const linkColor = isDark
    ? (isOwn ? "#93C5FD" : "#60A5FA")
    : (isOwn ? "#BFDBFE" : "#2563EB");

  // Linkify http(s) URLs, phone numbers and emails — and turn /Name folder
  // tokens into clickable chips — in a plain text chunk.
  const renderLinks = (chunk: string, keyPrefix: number) => (
    <span key={keyPrefix}>{renderTextWithFolderTokens(chunk, folderRefs, linkColor, isDark, isOwn)}</span>
  );

  if (!hasMentions) return <>{renderLinks(text, 0)}</>;

  // Split by @mentions first, then linkify the non-mention chunks.
  const pieces: React.ReactNode[] = [];
  let lastIdx = 0;
  const regex = new RegExp(MENTION_REGEX.source, "gu");
  let match: RegExpExecArray | null;
  const mentionColor = isDark
    ? (isOwn ? "#93C5FD" : "#60A5FA")
    : (isOwn ? "#BFDBFE" : "#3B82F6");
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIdx) pieces.push(renderLinks(text.slice(lastIdx, match.index), lastIdx));
    pieces.push(
      <span key={`m-${match.index}`} style={{ color: mentionColor, fontWeight: 600 }}>
        {match[1]}
      </span>
    );
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < text.length) pieces.push(renderLinks(text.slice(lastIdx), lastIdx));
  return <>{pieces}</>;
}

// ─── Bubble memo comparators ────────────────────────────────
// Cheap structural checks for the in-place-updating arrays so React.memo can
// skip re-rendering a bubble when no render-affecting data prop changed.

/** Reactions update in place (live). Compare by length + emoji + userId order. */
function reactionsEqual(a: DisplayMessage["reactions"], b: DisplayMessage["reactions"]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].emoji !== b[i].emoji || a[i].userId !== b[i].userId) return false;
  }
  return true;
}

/** Attachments: compare by length + id/url/name/base64/upload-relevant fields. */
function attachmentsEqual(a?: ChatAttachment[], b?: ChatAttachment[]): boolean {
  if (a === b) return true;
  const la = a?.length || 0;
  const lb = b?.length || 0;
  if (la !== lb) return false;
  for (let i = 0; i < la; i++) {
    const x = a![i];
    const y = b![i];
    if (x === y) continue;
    if (
      x.url !== y.url ||
      x.base64Data !== y.base64Data ||
      x.name !== y.name ||
      x.preview_url !== y.preview_url ||
      (x.mime_type || x.mimeType) !== (y.mime_type || y.mimeType)
    ) return false;
  }
  return true;
}

function folderRefsEqual(a?: { id: string; name: string }[], b?: { id: string; name: string }[]): boolean {
  if (a === b) return true;
  const la = a?.length || 0;
  const lb = b?.length || 0;
  if (la !== lb) return false;
  for (let i = 0; i < la; i++) {
    if (a![i].id !== b![i].id || a![i].name !== b![i].name) return false;
  }
  return true;
}

function stringArrayEqual(a?: string[], b?: string[]): boolean {
  if (a === b) return true;
  const la = a?.length || 0;
  const lb = b?.length || 0;
  if (la !== lb) return false;
  for (let i = 0; i < la; i++) if (a![i] !== b![i]) return false;
  return true;
}

/** True when two messages are identical for every render-affecting field. */
function displayMessageEqual(a: DisplayMessage, b: DisplayMessage): boolean {
  if (a === b) return true;
  return (
    a.id === b.id &&
    a.text === b.text &&
    a.edited === b.edited &&
    a.deleted === b.deleted &&
    a.timestamp === b.timestamp &&
    a.createdAt === b.createdAt &&
    a.isOwn === b.isOwn &&
    a.replyToId === b.replyToId &&
    a.channel === b.channel &&
    a.wa_status === b.wa_status &&
    a.wa_error === b.wa_error &&
    a.wa_retry_pending === b.wa_retry_pending &&
    a.email_status === b.email_status &&
    a.senderName === b.senderName &&
    a.senderRole === b.senderRole &&
    a.senderType === b.senderType &&
    a.senderPhotoUrl === b.senderPhotoUrl &&
    a.senderAvatarSeed === b.senderAvatarSeed &&
    a.event === b.event &&
    reactionsEqual(a.reactions, b.reactions) &&
    attachmentsEqual(a.attachments, b.attachments) &&
    folderRefsEqual(a.folderRefs, b.folderRefs) &&
    stringArrayEqual(a.mentions, b.mentions)
  );
}

/** Memo comparator: re-render (return false) when ANY render-affecting data prop
 * changes. Stable callback props (onReact/onReply/.../onJumpToMessage) are
 * intentionally NOT compared — the host passes useCallback-stable refs. */
function chatBubbleAreEqual(prev: ChatBubbleProps, next: ChatBubbleProps): boolean {
  return (
    prev.position === next.position &&
    prev.showTimestamp === next.showTimestamp &&
    prev.isDark === next.isDark &&
    prev.readStatus === next.readStatus &&
    prev.createdAt === next.createdAt &&
    prev.uploadProgress === next.uploadProgress &&
    prev.isPinned === next.isPinned &&
    displayMessageEqual(prev.msg, next.msg) &&
    ((!prev.replySource && !next.replySource) ||
      (!!prev.replySource && !!next.replySource && displayMessageEqual(prev.replySource, next.replySource)))
  );
}

type ChatBubbleProps = {
  msg: DisplayMessage;
  position: "solo" | "first" | "middle" | "last";
  showTimestamp: boolean;
  replySource?: DisplayMessage;
  onReact: (msgId: string, emoji: string) => void;
  onReply: (msgId: string) => void;
  onDelete: (msgId: string) => void;
  onEdit?: (msgId: string, newText: string) => void;
  onPreview: (attachment: { name: string; type: string; url: string; gcs_path?: string }) => void;
  onOpenGallery: (list: ChatAttachment[], att: ChatAttachment) => void;
  isDark?: boolean;
  uploadProgress?: number;
  readStatus?: "sent" | "read_some" | "read_all" | "delivered" | "read" | "failed";
  onReadInfoClick?: (msgId: string) => void;
  onRetry?: (msgId: string) => void | Promise<void>;
  createdAt?: string;
  isPinned?: boolean;
  onPin?: (msgId: string) => void;
  onUnpin?: (msgId: string) => void;
  onJumpToMessage?: (msgId: string) => void;
};

function ChatBubbleImpl({
  msg,
  position,
  showTimestamp,
  replySource,
  onReact,
  onReply,
  onDelete,
  onEdit,
  onPreview,
  onOpenGallery,
  isDark,
  readStatus,
  onReadInfoClick,
  onRetry,
  createdAt,
  uploadProgress,
  isPinned,
  onPin,
  onUnpin,
  onJumpToMessage,
}: ChatBubbleProps) {
  const [hovered, setHovered] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [editText, setEditText] = useState("");
  const [fullPickerAnchor, setFullPickerAnchor] = useState<{ x: number; y: number } | null>(null);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const editInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!showDropdown) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showDropdown]);

  useEffect(() => {
    if (!showDropdown || !triggerRef.current) {
      setDropdownPos(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    requestAnimationFrame(() => {
      const menuW = dropdownRef.current?.offsetWidth || 260;
      const menuH = dropdownRef.current?.offsetHeight || 200;
      const top = rect.bottom + 4 + menuH > window.innerHeight
        ? rect.top - menuH - 4
        : rect.bottom + 4;
      const spaceRight = window.innerWidth - rect.left;
      const left = spaceRight >= menuW ? rect.left : rect.right - menuW;
      setDropdownPos({ top, left: Math.max(4, left) });
    });
  }, [showDropdown]);

  useEffect(() => {
    if (isEditing) editInputRef.current?.focus();
  }, [isEditing]);

  const canEdit = msg.isOwn && !msg.deleted && !!createdAt &&
    (Date.now() - new Date(createdAt).getTime()) < 60 * 60 * 1000;

  const startEditing = () => {
    setEditText(msg.text || "");
    setIsEditing(true);
    setShowDropdown(false);
    setHovered(false);
  };

  const saveEdit = () => {
    const trimmed = editText.trim();
    if (trimmed && trimmed !== msg.text) onEdit?.(msg.id, trimmed);
    setIsEditing(false);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditText("");
  };

  // Failed WhatsApp sends get a distinct tint so they stand out from delivered ones:
  // amber while queued for automatic resend, red once we've given up (manual retry).
  // Any OUTBOUND message counts — rappels automatiques (« system ») et envois des
  // collègues compris : un « Non reçu » doit sauter aux yeux de tout le monde,
  // pas seulement de l'auteur.
  const waFailed = msg.channel === "whatsapp" && msg.wa_status === "failed" && msg.senderType !== "client";
  const waRetryPending = waFailed && !!msg.wa_retry_pending;
  // Retenu faute de fenêtre 24h : part dès que le client répond à la demande d'accord.
  const waAwaitingConsent = msg.channel === "whatsapp" && msg.wa_status === "awaiting_consent";
  // Own-message text stays white, so failed bubbles use dark amber/red tints (not
  // pale ones) to keep the text readable in both light and dark mode.
  const bubbleBg = waRetryPending
    ? "#B45309"   // amber-700 — queued for automatic resend
    : waFailed
      ? "#B91C1C" // red-700 — send failed, manual retry
      : isDark
        ? (msg.isOwn ? "#525252" : "#404040")
        : (msg.isOwn ? "#000000" : "#F5F5F5");
  const radius = getBubbleRadius(msg.isOwn, position);

  const grouped = msg.reactions.reduce((acc, r) => {
    if (!acc[r.emoji]) acc[r.emoji] = { count: 0, isMine: false };
    acc[r.emoji].count++;
    if (r.userId === "me") acc[r.emoji].isMine = true;
    return acc;
  }, {} as Record<string, { count: number; isMine: boolean }>);
  const hasReactions = Object.keys(grouped).length > 0;

  // Split attachments into images / audio / pdf / other so a single message can
  // render every file (album + audio players + pdf cards + file rows).
  const attachments = msg.attachments || [];
  const imageAtts = attachments.filter(isImageAtt);
  const audioAtts = attachments.filter(isAudioAtt);
  const videoAtts = attachments.filter(isVideoAtt);
  const docAtts = attachments.filter((a) => !isImageAtt(a) && !isAudioAtt(a) && !isVideoAtt(a));
  const downloadAtt = (a: ChatAttachment) => downloadFile(a.base64Data || a.url || "", a.name || (isImageAtt(a) ? "image.png" : isPdfAtt(a) ? "document.pdf" : isVideoAtt(a) ? "video.mp4" : "fichier"));

  if (msg.deleted) {
    return (
      <div style={{ padding: "8px 12px", background: bubbleBg, borderRadius: radius, maxWidth: "100%", opacity: 0.7 }}>
        <span className="text-h2 italic" style={{ fontFamily: "Poppins", color: isDark ? "#6B7280" : "#a3a3a3" }}>Ce message a été supprimé</span>
      </div>
    );
  }

  return (
    <div
      className="relative"
      style={{ minWidth: 80, width: "fit-content", paddingBottom: hasReactions ? 14 : 0 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { if (!showDropdown && !isEditing) setHovered(false); }}
    >
      {/* Dropdown trigger */}
      {(hovered || showDropdown) && (
        <div className="absolute z-20" style={{ top: 2, right: 2 }}>
          <button
            ref={triggerRef}
            onClick={() => setShowDropdown((v) => !v)}
            className="flex items-center justify-center"
            style={{
              width: 28, height: 28, borderRadius: 6,
              background: (isDark || msg.isOwn) ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.07)",
            }}
          >
            <svg width="12" height="7" viewBox="0 0 10 6" fill="none">
              <path d="M1 1L5 5L9 1" stroke={(isDark || msg.isOwn) ? "#FFFFFF" : "#525252"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {showDropdown && createPortal(
        <div
          ref={dropdownRef}
          className="flex flex-col"
          style={{
            position: "fixed",
            zIndex: 10001, // above BaseModal (z-[10000])
            top: dropdownPos?.top ?? -9999,
            left: dropdownPos?.left ?? -9999,
            opacity: dropdownPos ? 1 : 0,
            background: isDark ? "#2D2D2D" : "#FFFFFF",
            boxShadow: isDark ? "0px 6px 20px rgba(0,0,0,0.4)" : "0px 6px 20px rgba(0,0,0,0.12)",
            borderRadius: 10,
            border: `1px solid ${isDark ? "#404040" : "#F0F0F0"}`,
            overflow: "hidden",
            minWidth: 190,
          }}
        >
          {/* Quick emoji row — hidden for email channel */}
          {msg.channel !== "email" && (
          <div className="flex flex-row items-center" style={{ padding: "8px 8px 6px", gap: 1, borderBottom: `1px solid ${isDark ? "#404040" : "#F5F5F5"}` }}>
            {EMOJI_LIST.slice(0, 7).map((emoji) => (
              <button
                key={emoji}
                onClick={() => { onReact(msg.id, emoji); setShowDropdown(false); setHovered(false); }}
                className={`${isDark ? "hover:bg-white/10" : "hover:bg-neutral-100"} rounded-lg flex items-center justify-center`}
                style={{ width: 30, height: 30, fontSize: 17, flexShrink: 0 }}
              >
                {emoji}
              </button>
            ))}
            <button
              onClick={(e) => {
                const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                const x = Math.min(r.left, window.innerWidth - 360);
                const y = r.bottom + 6;
                setFullPickerAnchor({ x, y });
                setShowDropdown(false);
              }}
              className={`${isDark ? "hover:bg-white/10" : "hover:bg-neutral-100"} rounded-lg flex items-center justify-center`}
              style={{ width: 30, height: 30, fontSize: 15, flexShrink: 0, color: isDark ? "#A3A3A3" : "#737373" }}
            >
              +
            </button>
          </div>
          )}

          {/* Actions */}
          <button
            onClick={() => { setForwardOpen(true); setShowDropdown(false); setHovered(false); }}
            className={`flex items-center ${isDark ? "hover:bg-white/5" : "hover:bg-neutral-50"} w-full`}
            style={{ padding: "9px 14px", gap: 10 }}
          >
            <svg width={14} height={14} viewBox="0 0 16 16" fill="none"><path d="M9 4l4 4-4 4M13 8H3.5" stroke={isDark ? "#D4D4D4" : "#525252"} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: isDark ? "#F5F5F5" : "#1E1E1E" }}>Transférer</span>
          </button>
          <button
            onClick={() => { onReply(msg.id); setShowDropdown(false); setHovered(false); }}
            className={`flex items-center ${isDark ? "hover:bg-white/5" : "hover:bg-neutral-50"} w-full`}
            style={{ padding: "9px 14px", gap: 10 }}
          >
            <Icon1 name="response" size={14} color={isDark ? "#D4D4D4" : "#525252"} />
            <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: isDark ? "#F5F5F5" : "#1E1E1E" }}>Répondre</span>
          </button>

          {(onPin || onUnpin) && (
            <button
              onClick={() => { (isPinned ? onUnpin : onPin)?.(msg.id); setShowDropdown(false); setHovered(false); }}
              className={`flex items-center ${isDark ? "hover:bg-white/5" : "hover:bg-neutral-50"} w-full`}
              style={{ padding: "9px 14px", gap: 10 }}
            >
              <span style={{ display: "flex", width: 14, justifyContent: "center" }}>
                <PinGlyph dark={isDark} size={14} />
              </span>
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: isDark ? "#F5F5F5" : "#1E1E1E" }}>
                {isPinned ? "Détacher" : "Épingler"}
              </span>
            </button>
          )}

          {msg.isOwn && readStatus !== undefined && onReadInfoClick && (
            <button
              onClick={() => { onReadInfoClick(msg.id); setShowDropdown(false); setHovered(false); }}
              className={`flex items-center ${isDark ? "hover:bg-white/5" : "hover:bg-neutral-50"} w-full`}
              style={{ padding: "9px 14px", gap: 10 }}
            >
              <Icon1 name="eye" size={14} color={isDark ? "#D4D4D4" : "#525252"} />
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: isDark ? "#F5F5F5" : "#1E1E1E" }}>Infos du message</span>
            </button>
          )}

          {canEdit && (
            <button
              onClick={startEditing}
              className={`flex items-center ${isDark ? "hover:bg-white/5" : "hover:bg-neutral-50"} w-full`}
              style={{ padding: "9px 14px", gap: 10 }}
            >
              <Icon1 name="modify" size={14} color={isDark ? "#D4D4D4" : "#525252"} />
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: isDark ? "#F5F5F5" : "#1E1E1E" }}>Modifier</span>
            </button>
          )}

          {msg.isOwn && (
            <>
              <div style={{ height: 1, background: isDark ? "#404040" : "#F5F5F5" }} />
              <button
                onClick={() => { onDelete(msg.id); setShowDropdown(false); setHovered(false); }}
                className={`flex items-center ${isDark ? "hover:bg-red-900/20" : "hover:bg-red-50"} w-full`}
                style={{ padding: "9px 14px", gap: 10 }}
              >
                <Icon1 name="delete" size={14} color="#ef4444" />
                <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#ef4444" }}>Supprimer</span>
              </button>
            </>
          )}
        </div>,
        document.body,
      )}

      <div style={{ background: bubbleBg, borderRadius: radius, boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.08)", overflow: "hidden" }}>
        {replySource && (
          <div
            onClick={onJumpToMessage && msg.replyToId ? (e) => { e.stopPropagation(); onJumpToMessage(msg.replyToId!); } : undefined}
            style={{
              margin: "6px 8px 0",
              padding: "6px 10px",
              background: isDark
                ? (msg.isOwn ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.05)")
                : (msg.isOwn ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.03)"),
              borderRadius: 8,
              borderLeft: `3px solid ${isDark ? "#D4D4D4" : "#525252"}`,
              maxWidth: 280,
              cursor: onJumpToMessage && msg.replyToId ? "pointer" : undefined,
            }}
          >
            <div className="text-h4 font-semibold" style={{ fontFamily: "Poppins", color: isDark ? "#D4D4D4" : "#525252", marginBottom: 2 }}>
              {replySource.senderName}
            </div>
            <div
              className={isDark ? "text-h4" : "text-h4 text-neutral-400"}
              style={{ fontFamily: "Poppins", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...(isDark ? { color: "#6B7280" } : {}) }}
            >
              {replySource.deleted ? "Ce message a été supprimé" : replySource.text || "📎 Pièce jointe"}
            </div>
          </div>
        )}

        {msg.event?.kind === "call_3cx" && (
          <CallEventCard event={msg.event} isOwn={msg.isOwn} isDark={!!isDark} />
        )}

        {isEditing ? (
          <div style={{ padding: "8px 12px 6px" }}>
            <textarea
              ref={editInputRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                if (e.key === "Escape") cancelEdit();
              }}
              className="w-full outline-none resize-none bg-transparent text-h2"
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                lineHeight: "20px",
                color: isDark ? "#FFFFFF" : ((msg.isOwn || waFailed) ? "#FFFFFF" : "#000000"),
                minHeight: 40,
                whiteSpace: "pre-wrap",
              }}
            />
            <div className="flex justify-end items-center" style={{ gap: 8, marginTop: 6 }}>
              <button
                onClick={cancelEdit}
                className="text-h5 hover:underline"
                style={{ fontFamily: "Poppins", fontWeight: 500, color: isDark ? "#A1A1A1" : (msg.isOwn ? "#A3A3A3" : "#6B7280") }}
              >
                Annuler
              </button>
              <button
                onClick={saveEdit}
                className="text-h5 font-semibold hover:underline"
                style={{ fontFamily: "Poppins", color: isDark ? "#FFFFFF" : ((msg.isOwn || waFailed) ? "#FFFFFF" : "#000000") }}
              >
                Sauvegarder
              </button>
            </div>
          </div>
        ) : (
          msg.text && (
            <div style={{ padding: "8px 12px 6px" }}>
              <span
                className="text-h2"
                style={{ fontFamily: "Poppins", fontWeight: 400, lineHeight: "20px", color: isDark ? ((msg.isOwn || waFailed) ? "#FFFFFF" : "#E5E5E5") : ((msg.isOwn || waFailed) ? "#FFFFFF" : "#000000"), whiteSpace: "pre-wrap", overflowWrap: "break-word", wordBreak: "break-word" }}
              >
                <Linkify text={msg.text} isOwn={msg.isOwn} isDark={isDark} mentionIds={msg.mentions} folderRefs={msg.folderRefs} />
              </span>
            </div>
          )
        )}

        {/* Images — WhatsApp-style album (with optimistic upload overlay) */}
        {imageAtts.length > 0 && (
          uploadProgress !== undefined && imageAtts.length === 1 ? (
            <div style={{ padding: "4px" }}>
              <div style={{ width: 240, height: 200, borderRadius: 8, overflow: "hidden", position: "relative" }}>
                <img
                  src={imageAtts[0].base64Data || imageAtts[0].url}
                  alt={imageAtts[0].name || "image"}
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                />
                <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="48" height="48" viewBox="0 0 48 48">
                    <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
                    <circle
                      cx="24" cy="24" r="20" fill="none" stroke="#FFFFFF" strokeWidth="3"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 20}`}
                      strokeDashoffset={`${2 * Math.PI * 20 * (1 - (uploadProgress || 0) / 100)}`}
                      transform="rotate(-90 24 24)"
                      style={{ transition: "stroke-dashoffset 0.3s ease" }}
                    />
                    <text x="24" y="24" textAnchor="middle" dominantBaseline="central" fill="#FFFFFF" fontSize="11" fontFamily="Poppins" fontWeight="500">
                      {uploadProgress}%
                    </text>
                  </svg>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ padding: 4 }}>
              <ImageAlbum
                images={imageAtts}
                hovered={hovered}
                onOpen={(a) => onOpenGallery(imageAtts, a)}
                onDownload={(a) => downloadAtt(a)}
              />
            </div>
          )
        )}

        {/* Videos — inline players */}
        {videoAtts.map((a, i) => (
          <div key={i} style={{ padding: 4 }}>
            <VideoCard att={a} onDownload={(x) => downloadAtt(x)} />
          </div>
        ))}

        {/* Documents — PDF cards or generic file rows */}
        {docAtts.map((a, i) =>
          isPdfAtt(a) ? (
            <div key={i} style={{ padding: 4 }}>
              <PdfCard
                att={a}
                isOwn={msg.isOwn}
                dark={isDark}
                onOpen={(x) => onOpenGallery([x], x)}
                onDownload={(x) => downloadAtt(x)}
              />
            </div>
          ) : (
            <div
              key={i}
              className="flex flex-row items-center cursor-pointer"
              style={{ padding: "8px 12px", gap: 10 }}
              onClick={() => a.url && onPreview({ name: a.name || "Fichier", type: "file", url: a.url, gcs_path: a.gcs_path })}
            >
              <Icon1 name="file" size={16} color={isDark ? (msg.isOwn ? "#D4D4D4" : "#E5E5E5") : ((msg.isOwn || waFailed) ? "#FFFFFF" : "#000000")} />
              <span className="text-h2" style={{ fontFamily: "Poppins", fontWeight: 500, color: isDark ? ((msg.isOwn || waFailed) ? "#FFFFFF" : "#E5E5E5") : ((msg.isOwn || waFailed) ? "#FFFFFF" : "#000000"), flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {a.name || "Fichier"}
              </span>
            </div>
          )
        )}

        {/* Audio — one player per audio attachment */}
        {audioAtts.map((a, i) =>
          a.url ? (
            <Suspense key={i} fallback={<div style={{ padding: "8px 12px", width: 320, height: 48 }} />}>
              <AudioPlayer src={a.url} isOwn={msg.isOwn} isDark={!!isDark} />
            </Suspense>
          ) : null
        )}

        {showTimestamp && !isEditing && (
          <div className="flex justify-end items-center" style={{ padding: "0 12px 6px", gap: 4 }}>
            {msg.channel === "whatsapp" && (
              <span className="text-h5" style={{ fontFamily: "Poppins", fontWeight: 500, color: "#25D366", fontSize: 10 }}>
                via WhatsApp
              </span>
            )}
            {msg.channel === "email" && (
              <span className="text-h5" style={{ fontFamily: "Poppins", fontWeight: 500, color: "#3B82F6", fontSize: 10 }}>
                via Email
              </span>
            )}
            {msg.channel === "sms" && (
              <span className="text-h5" style={{ fontFamily: "Poppins", fontWeight: 500, color: "#8B5CF6", fontSize: 10 }}>
                via SMS
              </span>
            )}
            {msg.edited && (
              <span className="text-h5" style={{ fontFamily: "Poppins", color: isDark ? "#6B7280" : (msg.isOwn ? "#A3A3A3" : "#A1A1A1"), fontStyle: "italic" }}>
                modifié
              </span>
            )}
            {isPinned && <PinGlyph dark={isDark} isOwn={msg.isOwn} size={11} />}
            <span className="text-h5" style={{ fontFamily: "Poppins", fontWeight: 500, color: isDark ? (msg.isOwn ? "#D4D4D4" : "#6B7280") : (msg.isOwn ? "#A3A3A3" : "#A1A1A1") }}>
              {msg.timestamp}
            </span>
            {msg.isOwn && readStatus && (
              <button
                onClick={() => onReadInfoClick?.(msg.id)}
                className="flex items-center"
                style={{ padding: 2, borderRadius: 4, opacity: onReadInfoClick ? 1 : 0.7 }}
                title={readStatus === "failed" ? (msg.wa_error || "Message non envoyé") : "Voir qui a lu"}
              >
                <ReadTicks status={readStatus} isDark={isDark} />
              </button>
            )}
          </div>
        )}

        {waAwaitingConsent && (
          <div className={`flex ${msg.isOwn ? "justify-end" : "justify-start"} items-center`} style={{ padding: "0 12px 6px" }}>
            <span
              className="text-h5"
              style={{ fontFamily: "Poppins", fontWeight: 600, color: "#B45309", fontSize: 10 }}
              title="Fenêtre de 24 h fermée : ce message part automatiquement dès que le client répond à la demande d'accord"
            >
              En attente de l'accord du client — part dès sa réponse
            </span>
          </div>
        )}

        {/* Failed WhatsApp send — surface it in the thread. While queued for automatic
            resend it shows amber "en attente"; once we've given up, red "Non envoyé". */}
        {waFailed && (
          <div className={`flex ${msg.isOwn ? "justify-end" : "justify-start"} items-center flex-wrap`} style={{ padding: "0 12px 6px", gap: 6 }}>
            {waRetryPending ? (
              <span
                className="text-h5 flex items-center"
                style={{ fontFamily: "Poppins", fontWeight: 600, color: "#B45309", fontSize: 10, gap: 3 }}
                title={msg.wa_error || undefined}
              >
                ⏳ En attente de reconnexion — renvoi automatique
              </span>
            ) : (
              <>
                <span
                  className="text-h5"
                  style={{ fontFamily: "Poppins", fontWeight: 600, color: "#EF4444", fontSize: 10 }}
                  title={msg.wa_failed_reason || msg.wa_error || undefined}
                >
                  ⚠ Non reçu{(msg.wa_failed_reason || msg.wa_error) ? ` — ${msg.wa_failed_reason || msg.wa_error}` : ""}
                </span>
                {onRetry && (
                  <button
                    onClick={async () => {
                      if (isRetrying) return;
                      setIsRetrying(true);
                      try { await onRetry(msg.id); } finally { setIsRetrying(false); }
                    }}
                    disabled={isRetrying}
                    className="flex items-center"
                    style={{
                      padding: "1px 8px",
                      borderRadius: 6,
                      gap: 4,
                      border: `1px solid ${isRetrying ? "#FCA5A5" : "#EF4444"}`,
                      color: isRetrying ? "#FCA5A5" : "#EF4444",
                      fontFamily: "Poppins",
                      fontWeight: 600,
                      fontSize: 10,
                      cursor: isRetrying ? "default" : "pointer",
                    }}
                    title="Renvoyer ce message"
                  >
                    {isRetrying ? "Envoi…" : "Réessayer"}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {hasReactions && (
        <div
          className="flex flex-row flex-wrap absolute"
          style={{ bottom: 0, ...(msg.isOwn ? { right: 8 } : { left: 8 }), gap: 2 }}
        >
          {Object.entries(grouped).map(([emoji, { count, isMine }]) => (
            <ReactionPill key={emoji} emoji={emoji} count={count} isMine={isMine} onToggle={() => onReact(msg.id, emoji)} />
          ))}
        </div>
      )}

      {/* Full emoji picker portal */}
      {forwardOpen && (
        <ForwardModal
          message={msg as any}
          isDark={isDark}
          onClose={() => setForwardOpen(false)}
        />
      )}

      {fullPickerAnchor && createPortal(
        <>
          <div
            className="fixed inset-0 z-[10001]"
            onClick={() => setFullPickerAnchor(null)}
          />
          <div
            style={{ position: "fixed", left: fullPickerAnchor.x, top: fullPickerAnchor.y, zIndex: 10002 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Picker
              data={emojiData}
              locale="fr"
              theme={isDark ? "dark" : "light"}
              previewPosition="none"
              skinTonePosition="none"
              onEmojiSelect={(emoji: { native: string }) => {
                onReact(msg.id, emoji.native);
                setFullPickerAnchor(null);
              }}
            />
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// Per-message bubble — memoized so reactions/read-status/pin/etc. on ONE message
// don't re-render every other bubble. Comparator covers all live-updating data.
const ChatBubble = memo(ChatBubbleImpl, chatBubbleAreEqual);

// ─── Attachment Preview Modal ────────────────────────────────

function AttachmentPreviewModal({
  name,
  url,
  gcsPath,
  onClose,
  entityType,
  entityId,
  onPrev,
  onNext,
  counter,
}: {
  name: string;
  url: string;
  gcsPath?: string;
  onClose: () => void;
  entityType?: "dossier" | "ordre";
  entityId?: string;
  onPrev?: () => void;
  onNext?: () => void;
  counter?: string;
}) {
  const isImage = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i.test(name) || url.startsWith("blob:") || url.startsWith("data:image");
  const isPdf = /\.pdf$/i.test(name);
  const [savingToFolder, setSavingToFolder] = useState(false);

  const canSaveToFolder = !!entityType && !!entityId;

  const handleSaveToFolder = async () => {
    if (!canSaveToFolder || savingToFolder) return;
    setSavingToFolder(true);
    try {
      let updated;
      if (gcsPath) {
        // Server-side copy — avoids browser CORS on the GCS signed URL
        if (entityType === "ordre") {
          updated = await ordresApi.importDocumentFromGcs(entityId!, gcsPath, name);
        } else {
          updated = await dossiersApi.importDocumentFromGcs(entityId!, gcsPath, name);
        }
      } else {
        const res = await fetch(url);
        const blob = await res.blob();
        const file = new File([blob], name, { type: blob.type || "application/octet-stream" });
        if (entityType === "ordre") {
          updated = await ordresApi.uploadDocument(entityId!, file, name);
        } else {
          updated = await dossiersApi.uploadDocument(entityId!, file, name);
        }
      }
      // Push the fresh entity into its store so the Documents tab updates without a refresh
      if (entityType === "ordre") {
        useOrdreStore.getState().updateOrdre(updated as any);
      } else {
        useDossierStore.getState().updateDossier(updated as any);
      }
      showNotification({ type: "success", message: "Document enregistré dans le dossier" });
    } catch (e) {
      console.error("Save to folder failed:", e);
      showNotification({ type: "error", message: "Échec de l'enregistrement" });
    } finally {
      setSavingToFolder(false);
    }
  };

  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  const zoomIn = () => setZoom((z) => Math.min(z * 1.3, 8));
  const zoomOut = () => setZoom((z) => Math.max(z / 1.3, 0.1));
  const resetView = () => { setZoom(1); setRotation(0); setPan({ x: 0, y: 0 }); };
  const rotateCW = () => setRotation((r) => (r + 90) % 360);
  const rotateCCW = () => setRotation((r) => (r - 90 + 360) % 360);

  const handleWheel = (e: React.WheelEvent) => { e.preventDefault(); e.deltaY < 0 ? zoomIn() : zoomOut(); };
  const handleMouseDown = (e: React.MouseEvent) => { if (e.button !== 0) return; setDragging(true); dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y }; };
  const handleMouseMove = (e: React.MouseEvent) => { if (!dragging) return; setPan({ x: dragStart.current.panX + (e.clientX - dragStart.current.x), y: dragStart.current.panY + (e.clientY - dragStart.current.y) }); };
  const handleMouseUp = () => setDragging(false);

  // Reset the view when switching to another item in a gallery.
  useEffect(() => { setZoom(1); setRotation(0); setPan({ x: 0, y: 0 }); }, [url]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowLeft" && onPrev) { onPrev(); return; }
      if (e.key === "ArrowRight" && onNext) { onNext(); return; }
      if (!isImage) return;
      if (e.key === "+" || e.key === "=") zoomIn();
      else if (e.key === "-") zoomOut();
      else if (e.key === "0") resetView();
      else if (e.key === "r") rotateCW();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isImage, onClose, onPrev, onNext]);

  const toolbarBtn = "flex items-center justify-center rounded-lg hover:bg-neutral-200 transition-colors";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.6)" }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: "80vw", maxWidth: 900, height: "85vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-row items-center justify-between px-5 py-3 border-b border-neutral-200">
          <span className="text-h2 font-semibold">{name}</span>
          <div className="flex flex-row items-center gap-2">
            {canSaveToFolder && (
              <button
                onClick={handleSaveToFolder}
                disabled={savingToFolder}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50 text-h3 font-medium text-neutral-600 disabled:opacity-50"
              >
                <Icon1 name="folder" size={14} color="#525252" />
                {savingToFolder ? "Enregistrement…" : "Enregistrer dans le dossier"}
              </button>
            )}
            <button
              onClick={() => downloadFile(url, name)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50 text-h3 font-medium text-neutral-600"
            >
              <Icon1 name="download" size={14} color="#525252" />
              Télécharger
            </button>
            <button onClick={onClose} className={toolbarBtn} style={{ width: 32, height: 32 }}>
              <Icon1 name="close" size={16} color="#525252" />
            </button>
          </div>
        </div>

        {/* Image toolbar */}
        {isImage && (
          <div className="flex flex-row items-center justify-center gap-1 px-4 py-2 border-b border-neutral-200 bg-neutral-50">
            <button onClick={zoomOut} className={toolbarBtn} style={{ width: 32, height: 32 }} title="Dézoomer (-)">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M4 8h8" stroke="#525252" strokeWidth={1.5} strokeLinecap="round" /></svg>
            </button>
            <span className="text-h4 text-neutral-500 w-12 text-center select-none">{Math.round(zoom * 100)}%</span>
            <button onClick={zoomIn} className={toolbarBtn} style={{ width: 32, height: 32 }} title="Zoomer (+)">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M8 4v8M4 8h8" stroke="#525252" strokeWidth={1.5} strokeLinecap="round" /></svg>
            </button>
            <div className="w-px h-5 bg-neutral-300 mx-1" />
            <button onClick={rotateCCW} className={toolbarBtn} style={{ width: 32, height: 32 }} title="Rotation gauche">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M2 6.5A5.5 5.5 0 0 1 12.95 5M2 2.5V6.5H6" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" /><circle cx="8" cy="9" r="4.5" stroke="#525252" strokeWidth={1.2} fill="none" /></svg>
            </button>
            <button onClick={rotateCW} className={toolbarBtn} style={{ width: 32, height: 32 }} title="Rotation droite (R)">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M14 6.5A5.5 5.5 0 0 0 3.05 5M14 2.5V6.5H10" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" /><circle cx="8" cy="9" r="4.5" stroke="#525252" strokeWidth={1.2} fill="none" /></svg>
            </button>
            <div className="w-px h-5 bg-neutral-300 mx-1" />
            <button onClick={resetView} className={toolbarBtn + " px-2 gap-1"} style={{ height: 32 }} title="Réinitialiser (0)">
              <svg width={14} height={14} viewBox="0 0 14 14" fill="none"><path d="M1 7a6 6 0 1 1 1.46 3.93" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" /><path d="M1 11V7.5H4.5" stroke="#525252" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" /></svg>
              <span className="text-h4 text-neutral-500">1:1</span>
            </button>
          </div>
        )}

        {/* Content */}
        <div
          className="flex-1 overflow-hidden flex items-center justify-center bg-neutral-50"
          style={{ cursor: isImage ? (dragging ? "grabbing" : "grab") : "default" }}
          onWheel={isImage ? handleWheel : undefined}
          onMouseDown={isImage ? handleMouseDown : undefined}
          onMouseMove={isImage ? handleMouseMove : undefined}
          onMouseUp={isImage ? handleMouseUp : undefined}
          onMouseLeave={isImage ? handleMouseUp : undefined}
        >
          {(onPrev || onNext) && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onPrev && onPrev(); }}
                className="absolute z-10 flex items-center justify-center rounded-full hover:bg-black/60"
                style={{ left: 12, top: "50%", transform: "translateY(-50%)", width: 40, height: 40, background: "rgba(0,0,0,0.45)", border: "none", cursor: "pointer" }}
                title="Précédent (←)"
              >
                {/* SVG en ligne : Icon1 n'a pas d'icône « chevron », la flèche était invisible. */}
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onNext && onNext(); }}
                className="absolute z-10 flex items-center justify-center rounded-full hover:bg-black/60"
                style={{ right: 12, top: "50%", transform: "translateY(-50%)", width: 40, height: 40, background: "rgba(0,0,0,0.45)", border: "none", cursor: "pointer" }}
                title="Suivant (→)"
              >
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
              </button>
              {counter && (
                <span
                  className="absolute z-10 select-none"
                  style={{ bottom: 12, left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.55)", color: "#FFFFFF", borderRadius: 999, padding: "3px 12px", fontFamily: "Poppins", fontSize: 12 }}
                >
                  {counter}
                </span>
              )}
            </>
          )}
          {isPdf ? (
            <iframe src={url} className="w-full h-full" style={{ border: "none" }} title={name} />
          ) : isImage ? (
            <img
              src={url}
              alt={name}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                transformOrigin: "center center",
                transition: dragging ? "none" : "transform 0.15s ease",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                userSelect: "none",
                pointerEvents: "none",
              }}
              draggable={false}
            />
          ) : (
            <div className="flex flex-col items-center gap-3 text-neutral-400">
              <Icon1 name="file" size={48} color="#D4D4D4" />
              <span className="text-h2 font-medium">Aperçu non disponible</span>
              <button
                onClick={() => downloadFile(url, name)}
                className="px-4 py-2 rounded-lg bg-indigo-500 text-white text-h3 font-medium hover:bg-indigo-600"
              >
                Télécharger le fichier
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main ChatPanel ─────────────────────────────────────────

export interface ChatPanelProps {
  discussionId?: string;
  internalDiscussionId?: string;
  entityType?: "dossier" | "ordre";
  entityId?: string;
  entityName?: string;
  members?: ChatMember[];
  onlineCount?: number;
  compact?: boolean;
  whatsappLinked?: boolean;
  whatsappProvider?: "wasender" | "twilio";
  clientChannel?: "whatsapp" | "email" | null;
  initialTab?: "client" | "privee";
  onToggleInfo?: () => void;
  /** When set (e.g. from the Discussions page), show quick-nav buttons in the
   * header to jump to the linked ordre / dossier. Omitted on the ordre/dossier
   * detail screens where the chat is already embedded in that entity. */
  entityLinks?: { ordreId?: string; dossierId?: string };
}

/** Numéro tel qu'on l'affiche dans l'entête : national si c'est un FR, brut sinon. */
function formatPhoneDisplay(phone?: string | null): string {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  const nat = digits.length === 11 && digits.startsWith("33") ? "0" + digits.slice(2) : digits.length === 10 ? digits : null;
  return nat ? nat.replace(/(\d{2})(?=\d)/g, "$1 ").trim() : phone;
}

/** Largeur du popover « Canal de communication », en px. */
const CHANNEL_POPOVER_WIDTH = 320;

/** Mobile français (06 / 07). */
function isFrenchMobile(phone?: string | null): boolean {
  if (!phone) return false;
  const digits = phone.replace(/\D/g, "");
  const nat = digits.startsWith("33") ? digits.slice(2) : digits.startsWith("0") ? digits.slice(1) : null;
  return !!nat && nat.length === 9 && (nat[0] === "6" || nat[0] === "7");
}

/** Ligne fixe française (01–05, 08, 09) : jamais joignable sur WhatsApp. */
function isFrenchLandline(phone?: string | null): boolean {
  if (!phone) return false;
  const digits = phone.replace(/\D/g, "");
  const nat = digits.startsWith("33") ? digits.slice(2) : digits.startsWith("0") ? digits.slice(1) : null;
  if (!nat || nat.length !== 9) return false;
  return nat[0] !== "6" && nat[0] !== "7";
}

export default function ChatPanel({
  discussionId,
  internalDiscussionId,
  entityType,
  entityId,
  entityName,
  members = [],
  onlineCount = 0,
  compact = false,
  whatsappLinked = false,
  whatsappProvider: whatsappProviderProp,
  clientChannel = null,
  initialTab = "client",
  onToggleInfo,
  entityLinks,
}: ChatPanelProps) {
  const [chatTab, setChatTab] = useState(initialTab);
  useEffect(() => { setChatTab(initialTab); }, [initialTab]);
  // Le fournisseur WhatsApp est celui de l'organisation : lu ici plutôt que
  // redonné par chaque écran (la modale ordre l'oubliait → pas de templates,
  // pas de blocage hors fenêtre, alors que la fiche ordre les avait).
  const orgWhatsappProvider = useDataStore((s) => s.orgData?.whatsapp?.provider);
  const whatsappProvider: "wasender" | "twilio" = whatsappProviderProp || orgWhatsappProvider || "wasender";
  const [channelOverride, setChannelOverride] = useState<"whatsapp" | "email" | null>(null);
  const channelTab = channelOverride || clientChannel || "whatsapp";
  const [showChannelInfo, setShowChannelInfo] = useState(false);
  // Sourdine CLIENT : le dossier est coupé de TOUTE communication sortante
  // (email + WhatsApp, automatique comme manuelle). Posée à la prise de RDV
  // interne ou ici ; elle ne se lève que manuellement. Rien à voir avec
  // muteDiscussion, qui ne coupe que les notifications internes d'un membre.
  // Sourdine du client : même état que la pastille des en-têtes (useClientMute).
  const { state: clientMute, busy: clientMuteBusy, setMuted: toggleClientMute } = useClientMute(discussionId);
  const channelInfoRef = useRef<HTMLDivElement>(null);
  // Decalage du popover de canal. Il s'ouvrait aligne a gauche sur sa pastille,
  // ce qui suffisait tant que la pastille ouvrait la ligne ; depuis que le
  // selecteur Client/Interne la precede, ses 320 px sortaient de l'ecran sur une
  // fenetre etroite. On le ramene dans la fenetre au moment de l'ouvrir.
  const [channelInfoShift, setChannelInfoShift] = useState(0);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [inputText, setInputText] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCurrentIndex, setSearchCurrentIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<{ name: string; type: string; url: string; gcs_path?: string } | null>(null);
  // Gallery lightbox: a list of attachments + the active index, so the user can
  // page between sibling images (or open a single PDF) of the same message.
  const [lightbox, setLightbox] = useState<{ list: ChatAttachment[]; idx: number } | null>(null);
  const openLightbox = useCallback((list: ChatAttachment[], att: ChatAttachment) => {
    setLightbox({ list, idx: Math.max(0, list.indexOf(att)) });
  }, []);
  const lightboxStep = useCallback((delta: number) => {
    setLightbox((lb) => (lb ? { ...lb, idx: (lb.idx + delta + lb.list.length) % lb.list.length } : lb));
  }, []);
  const [readInfoMsgId, setReadInfoMsgId] = useState<string | null>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const [folderQuery, setFolderQuery] = useState<string | null>(null);
  const [folderIndex, setFolderIndex] = useState(0);
  const [trackedFolders, setTrackedFolders] = useState<FolderRef[]>([]);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  // WhatsApp recipient number editing
  const [editingWaNumber, setEditingWaNumber] = useState(false);
  const [waNumberInput, setWaNumberInput] = useState("");
  const [waUpdateContact, setWaUpdateContact] = useState(false);
  const [savingWaNumber, setSavingWaNumber] = useState(false);
  const [stagedFile, setStagedFile] = useState<{ file: File; type: "image" | "file"; previewUrl?: string } | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mentionMenuRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const attachMenuRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [followLoading, setFollowLoading] = useState(false);
  const isDark = chatTab === "privee";
  const activeDiscussionId = chatTab === "client" ? discussionId : internalDiscussionId;

  // Document envoyé depuis l'onglet Documents (clic droit → « Envoyer au client ») : le chat
  // VISIBLE le pose dans sa saisie, côté Client. Le panneau large reste monté mais caché
  // sous 2xl : sans ce test il avalait le document à la place de la bulle flottante.
  const rootRef = useRef<HTMLDivElement>(null);
  const stagedFromDocs = useChatStageStore((s) => (s.pending?.discussionId === discussionId ? s.pending : null));
  useEffect(() => {
    if (!stagedFromDocs || !discussionId || !rootRef.current?.offsetParent) return;
    const p = useChatStageStore.getState().take(discussionId);
    if (!p) return;
    setChatTab("client");
    const previewUrl = p.file.type.startsWith("image/") ? URL.createObjectURL(p.file) : undefined;
    setStagedFile({ file: p.file, type: p.type, previewUrl });
    inputRef.current?.focus();
  }, [stagedFromDocs, discussionId]);
  const devOrg = useIsDevOrg();

  // Check if discussion is linked to WhatsApp or Email (from store or prop)
  const storeDiscussion = useMessagerieStore((s) => discussionId ? s.discussions.find((d) => d.id === discussionId) : undefined);
  const isWhatsappLinked = whatsappLinked || !!storeDiscussion?.whatsapp_phone;

  // Live WhatsApp availability: sending on the WhatsApp channel is blocked outright
  // rather than queued-and-failed after the fact — either because the session is
  // down, or because the org's send switch is off (both block server-side anyway).
  const waSessionStatus = useWhatsAppStatusStore((s) => s.status);
  const rawWaBlock = whatsAppBlock(waSessionStatus);
  // The session probe is WaSender-specific: the official provider has no long-lived
  // linked session to lose, so a "disconnected" verdict there is meaningless — and
  // once an org migrates, the retired WaSender session reports logged_out forever,
  // which would block a composer that actually works. The org's own send switch
  // ("disabled") is provider-agnostic and still applies.
  const waBlock: WhatsAppBlock =
    whatsappProvider === "wasender" || rawWaBlock.reason === "disabled"
      ? rawWaBlock
      : { blocked: false, reason: null };
  const waChannelBlocked =
    chatTab === "client" && channelTab === "whatsapp" && isWhatsappLinked && waBlock.blocked;
  const isEmailLinked = !!storeDiscussion?.client_email;
  // Destinataire du canal courant, affiché dans la pastille d'entête.
  const channelRecipientRaw = channelTab === "whatsapp"
    ? (isWhatsappLinked ? storeDiscussion?.whatsapp_phone || "" : "")
    : (storeDiscussion?.client_email || "");
  const channelRecipient = channelTab === "whatsapp"
    ? formatPhoneDisplay(channelRecipientRaw)
    : channelRecipientRaw;
  // Numéro fixe saisi comme WhatsApp : tout envoi échouera (63024). On prévient
  // dans le fil et on propose l'e-mail plutôt que de laisser écrire pour rien.
  const waLandline =
    chatTab === "client" && channelTab === "whatsapp" && isWhatsappLinked &&
    isFrenchLandline(storeDiscussion?.whatsapp_phone);
  // Autres contacts du client joignables sur un mobile : un fixe comme numéro WhatsApp
  // vient presque toujours d'un contact principal « standard de la société » alors qu'un
  // second contact a un 06 — on propose de basculer la conversation dessus en un clic.
  const landlineDossierId = entityLinks?.dossierId || storeDiscussion?.dossier_id || (entityType === "dossier" ? entityId : undefined);
  const [altMobileContacts, setAltMobileContacts] = useState<{ id: string; name: string; phone: string }[]>([]);
  const [switchingWaTo, setSwitchingWaTo] = useState<string | null>(null);
  useEffect(() => {
    if (!waLandline || !landlineDossierId) { setAltMobileContacts([]); return; }
    let cancelled = false;
    const current = (storeDiscussion?.whatsapp_phone || "").replace(/\D/g, "");
    dossiersApi.get(landlineDossierId).then((d) => {
      if (cancelled) return;
      const alts = (d?.contacts || [])
        .filter((c) => c.phone && !isFrenchLandline(c.phone) && c.phone.replace(/\D/g, "").slice(-9) !== current.slice(-9))
        .map((c) => ({ id: c.id, name: [c.prenom, c.nom].filter(Boolean).join(" ") || "ce contact", phone: c.phone! }));
      setAltMobileContacts(alts.slice(0, 3));
    }).catch(() => { if (!cancelled) setAltMobileContacts([]); });
    return () => { cancelled = true; };
  }, [waLandline, landlineDossierId, storeDiscussion?.whatsapp_phone]);
  const switchWaToContact = async (alt: { id: string; name: string; phone: string }) => {
    if (!discussionId) return;
    setSwitchingWaTo(alt.id);
    try {
      // Le numéro de la conversation seulement : la fiche du contact principal garde le sien.
      await messagerieApi.setWhatsappPhone(discussionId, alt.phone, false);
      showNotification({ type: "success", message: `Conversation WhatsApp basculée sur ${alt.name} (${formatPhoneDisplay(alt.phone)})` });
    } catch {
      showNotification({ type: "error", message: "Impossible de changer le numéro WhatsApp" });
    } finally {
      setSwitchingWaTo(null);
    }
  };

  // WhatsApp 24h conversation window: returns remaining hours (0 = closed)
  const clientMessages = useMessagerieStore((s) => discussionId ? s.messages[discussionId] : undefined);
  const whatsappWindowHours = useMemo(() => {
    if (!isWhatsappLinked) return 0;
    const now = Date.now();
    const threshold = 24 * 60 * 60 * 1000;

    let inboundTime: number | null = null;

    // Primary: check discussion field
    const lastInbound = storeDiscussion?.whatsapp_last_inbound_at;
    if (lastInbound) {
      inboundTime = new Date(lastInbound).getTime();
    }

    // Fallback: check most recent inbound WhatsApp message in loaded messages
    if (!inboundTime) {
      const msgs = clientMessages || [];
      for (let i = msgs.length - 1; i >= 0; i--) {
        const m = msgs[i];
        if (m.channel === "whatsapp" && m.sender_id?.startsWith("wa_")) {
          const ts = m.created_at || m.timestamp;
          if (ts) { inboundTime = new Date(ts).getTime(); break; }
        }
      }
    }

    if (!inboundTime) return 0;
    const remaining = threshold - (now - inboundTime);
    return remaining > 0 ? Math.ceil(remaining / 3600000) : 0;
  }, [isWhatsappLinked, storeDiscussion?.whatsapp_last_inbound_at, clientMessages]);
  const whatsappWindowOpen = whatsappWindowHours > 0;

  // On the official provider Meta refuses free text once the 24h customer service
  // window has closed — only an approved template may go out. Block the composer up
  // front instead of letting the send fail server-side with a 409. WaSender has no
  // such rule, so this never applies there.
  const waWindowBlocked =
    chatTab === "client" && channelTab === "whatsapp" && isWhatsappLinked &&
    whatsappProvider === "twilio" && !whatsappWindowOpen;
  // Anything that must stop a free-text send on the WhatsApp channel.
  const waSendBlocked = waChannelBlocked || waWindowBlocked;
  // Client en sourdine : le serveur refuse l'envoi (409), donc on coupe le
  // composeur du fil client plutôt que de laisser écrire pour rien.
  const clientMuted = !!clientMute?.muted;
  const clientMuteBlocked = chatTab === "client" && clientMuted;
  const composerBlocked = waSendBlocked || clientMuteBlocked;
  // Fenêtre fermée (Twilio) : le texte libre part quand même « via demande d'accord » —
  // mis en file côté serveur, le modèle pending_messages_request demande l'accord du
  // client et la file part dès sa réponse. Seuls la coupure WhatsApp et la sourdine bloquent.
  const waConsentMode = waWindowBlocked && !waChannelBlocked && !clientMuteBlocked;
  const composerInputBlocked = composerBlocked && !waConsentMode;

  // Unread counts for tab badges — subscribe to the full unreadCounts object
  // so we re-render whenever any count changes
  const unreadCounts = useMessagerieStore((s) => s.unreadCounts);
  const clientUnread = discussionId ? (unreadCounts[discussionId] || 0) : 0;
  const interneUnread = internalDiscussionId ? (unreadCounts[internalDiscussionId] || 0) : 0;

  // Store state
  const currentUserId = useMessagerieStore((s) => s.currentUserId);
  const storeMembers = useMessagerieStore((s) => s.members);
  const storeMessages = useMessagerieStore((s) => activeDiscussionId ? s.messages[activeDiscussionId] : undefined);
  const isLoadingMessages = useMessagerieStore((s) => s.isLoadingMessages);
  const fetchMessages = useMessagerieStore((s) => s.fetchMessages);
  const ensureDiscussionLoaded = useMessagerieStore((s) => s.ensureDiscussionLoaded);
  const ingestLiveMessages = useMessagerieStore((s) => s.ingestLiveMessages);
  const fetchReadReceipts = useMessagerieStore((s) => s.fetchReadReceipts);
  const sendMessageTo = useMessagerieStore((s) => s.sendMessageTo);
  const deleteMessageIn = useMessagerieStore((s) => s.deleteMessageIn);
  const updateMessageIn = useMessagerieStore((s) => s.updateMessageIn);
  const toggleReactionIn = useMessagerieStore((s) => s.toggleReactionIn);
  const readReceipts = useMessagerieStore((s) => (activeDiscussionId && s.readReceipts[activeDiscussionId]) || EMPTY_READ_RECEIPTS);
  const sendTyping = useMessagerieStore((s) => s.sendTyping);
  const clearTyping = useMessagerieStore((s) => s.clearTyping);
  const uploadProgress = useMessagerieStore((s) => s.uploadProgress);
  const isLoadingOlderMessages = useMessagerieStore((s) => s.isLoadingOlderMessages);
  const hasMoreMessages = useMessagerieStore((s) => s.hasMoreMessages);
  const fetchOlderMessages = useMessagerieStore((s) => s.fetchOlderMessages);
  const followDiscussion = useMessagerieStore((s) => s.followDiscussion);
  const unfollowDiscussion = useMessagerieStore((s) => s.unfollowDiscussion);
  const pinMessage = useMessagerieStore((s) => s.pinMessage);
  const unpinMessage = useMessagerieStore((s) => s.unpinMessage);
  const pendingScrollToMessageId = useMessagerieStore((s) => s.pendingScrollToMessageId);
  const clearPendingScrollToMessageId = useMessagerieStore((s) => s.clearPendingScrollToMessageId);

  // Follow state for entity discussions (ordre or dossier)
  const isEntity = entityType === "ordre" || entityType === "dossier";
  const discussionMembers = storeDiscussion?.members;
  const [localFollowState, setLocalFollowState] = useState<boolean | null>(null);

  // Backend auto-adds the sender as a follower if they aren't one already,
  // so flip the toggle optimistically as soon as a message is sent.
  const markFollowingOnSend = useCallback(() => {
    if (isEntity) setLocalFollowState(true);
  }, [isEntity]);

  // Le fil ouvert n'est pas forcément dans la liste du store, qui est paginée :
  // on le réclame nommément. Tant qu'on ne le faisait que pour l'état de suivi,
  // le reste — numéro WhatsApp, canal, avertissement « ligne fixe » — attendait
  // la liste, une quinzaine de secondes après l'ouverture.
  useEffect(() => {
    if (discussionId) ensureDiscussionLoaded(discussionId);
  }, [discussionId, ensureDiscussionLoaded]);

  useEffect(() => {
    if (!isEntity || !discussionId || !currentUserId || discussionMembers === undefined) return;
    setLocalFollowState(discussionMembers.includes(currentUserId));
  }, [isEntity, discussionId, currentUserId, discussionMembers]);

  const isFollowing = isEntity && (localFollowState ?? false);

  // Resolve real members from store (with photo_url) instead of relying on prop placeholders
  const resolvedMembers = useMemo(() => {
    if (!discussionMembers || discussionMembers.length === 0 || storeMembers.length === 0) return members;
    const mapped = discussionMembers
      .map((id) => storeMembers.find((m) => m.id === id))
      .filter((m): m is NonNullable<typeof m> => !!m);
    return mapped.length > 0 ? mapped : members;
  }, [discussionMembers, storeMembers, members]);
  const resolvedOnlineCount = useMemo(() => {
    if (!discussionMembers || storeMembers.length === 0) return onlineCount;
    return resolvedMembers.filter((m) => "online" in m && m.online).length;
  }, [resolvedMembers, discussionMembers, storeMembers, onlineCount]);

  // Typing indicator state
  const [typingNames, setTypingNames] = useState<string[]>([]);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Listen to typing subcollection
  useEffect(() => {
    if (!activeDiscussionId) { setTypingNames([]); return; }
    const typingCol = collection(firebase.db, "discussions", activeDiscussionId, "typing");
    const unsub = onSnapshot(typingCol, (snapshot) => {
      const now = Date.now();
      const names: string[] = [];
      snapshot.docs.forEach((d) => {
        if (d.id === currentUserId) return; // skip self
        // Ignore stale typing docs (>10s old)
        const ts = d.data().timestamp;
        if (ts?.toMillis && now - ts.toMillis() > 10000) return;
        const member = storeMembers.find((m) => m.id === d.id);
        if (member) names.push(member.name.split(" ")[0]); // first name only
      });
      setTypingNames(names);
    }, () => {});
    return () => { unsub(); setTypingNames([]); };
  }, [activeDiscussionId, currentUserId, storeMembers]);

  // Debounced typing signal
  const handleTypingSignal = useCallback(() => {
    if (!activeDiscussionId) return;
    sendTyping(activeDiscussionId);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      clearTyping(activeDiscussionId);
    }, 3000);
  }, [activeDiscussionId, sendTyping, clearTyping]);

  // Clear typing on unmount or discussion change
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [activeDiscussionId]);

  // Mention autocomplete: filter members matching the current @query
  const activeDiscussion = useMessagerieStore((s) => activeDiscussionId ? s.discussions.find((d) => d.id === activeDiscussionId) : undefined);

  // Pinned messages — O(1) lookup set + ordered list for the PinnedBar.
  const pinnedMessages = activeDiscussion?.pinned_messages || [];
  const pinnedIds = useMemo(() => new Set(pinnedMessages.map((p) => p.message_id)), [pinnedMessages]);

  // Transient highlight flash for jump-to-message.
  const [highlightedMsgId, setHighlightedMsgId] = useState<string | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isJumpingRef = useRef(false);

  const handlePin = useCallback((msgId: string) => {
    if (activeDiscussionId) pinMessage(activeDiscussionId, msgId);
  }, [activeDiscussionId, pinMessage]);
  const handleUnpin = useCallback((msgId: string) => {
    if (activeDiscussionId) unpinMessage(activeDiscussionId, msgId);
  }, [activeDiscussionId, unpinMessage]);

  // Jump to a message: scroll into view + 2s highlight flash. Pages back through
  // older messages (up to ~100 iters) if the target isn't loaded yet.
  const jumpToMessage = useCallback(async (msgId: string) => {
    if (!activeDiscussionId || isJumpingRef.current) return;
    isJumpingRef.current = true;
    const flash = () => {
      const el = document.getElementById(`chat-msg-${msgId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedMsgId(msgId);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => setHighlightedMsgId(null), 2000);
    };
    if (document.getElementById(`chat-msg-${msgId}`)) {
      flash();
      isJumpingRef.current = false;
      return;
    }
    const store = useMessagerieStore.getState;
    let attempts = 0;
    while (attempts < 100) {
      const { hasMoreMessages: hm, isLoadingOlderMessages: loading } = store();
      if (!hm[activeDiscussionId]) break;
      if (loading) { await new Promise((r) => setTimeout(r, 100)); continue; }
      await store().fetchOlderMessages(activeDiscussionId);
      attempts++;
      if ((store().messages[activeDiscussionId] || []).some((m) => m.id === msgId)) break;
    }
    requestAnimationFrame(() => { flash(); isJumpingRef.current = false; });
  }, [activeDiscussionId]);

  // Honor cross-component jump requests via the store (e.g. from another panel).
  useEffect(() => {
    if (pendingScrollToMessageId) {
      jumpToMessage(pendingScrollToMessageId);
      clearPendingScrollToMessageId();
    }
  }, [pendingScrollToMessageId, jumpToMessage, clearPendingScrollToMessageId]);

  useEffect(() => () => { if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current); }, []);
  const mentionCandidates = useMemo(() => {
    if (mentionQuery === null) return [];
    const pool = storeMembers.filter((m) => m.id !== currentUserId);
    if (!mentionQuery) return pool.slice(0, 6);
    return pool.filter((m) => matchesNamePrefix(m.name, mentionQuery)).slice(0, 6);
  }, [mentionQuery, storeMembers, currentUserId]);

  // Folder ("/" trigger) autocomplete over heric dossiers + ordres.
  const { results: folderResults, loading: folderLoading } = useFolderSearch(folderQuery);

  // Convert store messages to display messages — memoized on the source so the
  // derived array (and every bubble's `msg` prop) stays referentially stable
  // until the underlying store messages / members / user actually change.
  const messages: DisplayMessage[] = useMemo(
    () => (storeMessages || []).map((m) => toDisplayMessage(m, currentUserId, storeMembers)),
    [storeMessages, currentUserId, storeMembers],
  );

  // WhatsApp refuse de livrer ce client (63024 : CGU non acceptées, appli obsolète…)
  // alors qu'il a un mobile 06/07 : on lui envoie par SMS le lien WhatsApp du garage pour
  // qu'il écrive en premier. Le bandeau disparaît dès qu'il a répondu après l'échec.
  const [waInviteBusy, setWaInviteBusy] = useState(false);
  const waInvite = useMemo(() => {
    if (chatTab !== "client" || !isFrenchMobile(storeDiscussion?.whatsapp_phone)) return null;
    let lastFail = -1;
    messages.forEach((m, i) => {
      if (m.channel === "whatsapp" && m.wa_status === "failed" && m.senderType !== "client") lastFail = i;
    });
    if (lastFail < 0) return null;
    const after = messages.slice(lastFail + 1);
    if (after.some((m) => m.senderType === "client")) return null;
    const sent = (storeMessages || []).slice(lastFail + 1).filter((m) => m.sms_kind === "whatsapp_invite").pop();
    return { sentAt: sent ? (sent.created_at || sent.timestamp || null) : null };
  }, [chatTab, storeDiscussion?.whatsapp_phone, messages, storeMessages]);

  const sendWaInviteSms = async () => {
    if (!activeDiscussionId || waInviteBusy) return;
    setWaInviteBusy(true);
    try {
      await whatsappApi.sendInviteSms(activeDiscussionId);
      showNotification({ type: "success", message: "SMS envoyé avec le lien WhatsApp du garage" });
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || "Échec de l'envoi du SMS" });
    } finally {
      setWaInviteBusy(false);
    }
  };

  // Compute read status for a given message index (own messages only)
  // For WhatsApp messages, uses wa_status from Twilio delivery receipts
  // For internal messages, uses position comparison with read receipts
  const getReadStatus = (msg: DisplayMessage): "sent" | "read_some" | "read_all" | "delivered" | "read" | "failed" => {
    // WhatsApp outgoing messages: use Twilio delivery status
    if (msg.channel === "whatsapp" && msg.wa_status) {
      if (msg.wa_status === "queued" || msg.wa_status === "awaiting_consent") return "sent";
      return msg.wa_status;
    }

    // Email outgoing messages: use delivery status
    if (msg.channel === "email" && msg.email_status) {
      if (msg.email_status === "queued") return "sent";
      if (msg.email_status === "bounced") return "failed";
      return msg.email_status;
    }

    const msgIndex = messages.findIndex((m) => m.id === msg.id);
    if (msgIndex === -1) return "sent";
    const activeDisc = useMessagerieStore.getState().discussions.find((d) => d.id === activeDiscussionId);
    const otherMemberIds = (activeDisc?.members ?? []).filter((id) => id !== currentUserId);
    if (otherMemberIds.length === 0) return "sent";
    const otherReceipts = readReceipts.filter((r) => r.user_id !== currentUserId);
    let readCount = 0;
    for (const receipt of otherReceipts) {
      const receiptIndex = messages.findIndex((m) => m.id === receipt.last_read_message_id);
      if (receiptIndex >= msgIndex) readCount++;
    }
    if (readCount === 0) return "sent";
    if (readCount >= otherMemberIds.length) return "read_all";
    return "read_some";
  };

  // Track which discussion is actually visible (for WS auto-read logic)
  const setViewingDiscussion = useMessagerieStore((s) => s.setViewingDiscussion);
  useEffect(() => {
    if (activeDiscussionId) {
      setViewingDiscussion(activeDiscussionId);
      fetchMessages(activeDiscussionId);
    }
    return () => { setViewingDiscussion(null); };
  }, [activeDiscussionId, fetchMessages, setViewingDiscussion]);

  // Firestore real-time listener. The snapshot docs are merged straight into
  // the store: re-fetching the whole window over HTTP on every change meant one
  // round-trip per reaction, edit or read receipt.
  useEffect(() => {
    if (!activeDiscussionId) return;
    const q = query(
      collection(firebase.db, "discussions", activeDiscussionId, "messages"),
      orderBy("created_at", "desc"),
      fsLimit(50),
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const changes = snapshot.docChanges();
      if (changes.length === 0) return;
      const docs = snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      const removed = changes.filter((c) => c.type === "removed").map((c) => c.doc.id);
      ingestLiveMessages(activeDiscussionId, docs, removed);
    }, (err) => {
      console.warn("Chat Firestore listener error:", err);
    });
    return () => unsub();
  }, [activeDiscussionId, ingestLiveMessages]);

  // Read receipts move in their own sub-collection, so reactions and reads no
  // longer piggyback on the message listener.
  useEffect(() => {
    if (!activeDiscussionId) return;
    const q = query(collection(firebase.db, "discussions", activeDiscussionId, "read_receipts"));
    const unsub = onSnapshot(q, (snapshot) => {
      if (snapshot.docChanges().length === 0) return;
      fetchReadReceipts(activeDiscussionId);
    }, (err) => {
      console.warn("Chat read-receipts listener error:", err);
    });
    return () => unsub();
  }, [activeDiscussionId, fetchReadReceipts]);

  // Poll WasenderAPI for delivery statuses — DISABLED to test webhook after session restart
  // useEffect(() => {
  //   if (!activeDiscussionId || !isWhatsappLinked) return;
  //   let cancelled = false;
  //   const poll = () => {
  //     if (cancelled) return;
  //     whatsappApi.pollStatuses(activeDiscussionId).catch(() => {});
  //   };
  //   const t1 = setTimeout(poll, 2000);
  //   const interval = setInterval(poll, 15000);
  //   return () => { cancelled = true; clearTimeout(t1); clearInterval(interval); };
  // }, [activeDiscussionId, isWhatsappLinked]);

  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null;
  const isNearBottomRef = useRef(true);

  // Track whether user is near bottom of scroll
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll to bottom on new message
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    isNearBottomRef.current = true;
  }, [lastMessageId]);

  // Re-scroll on content resize (images loading, etc.) if near bottom
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (isNearBottomRef.current && el) {
        el.scrollTop = el.scrollHeight;
      }
    });
    // Observe all direct children so image loads trigger resize
    for (const child of Array.from(el.children)) ro.observe(child);
    const mo = new MutationObserver(() => {
      ro.disconnect();
      for (const child of Array.from(el.children)) ro.observe(child);
    });
    mo.observe(el, { childList: true });
    return () => { ro.disconnect(); mo.disconnect(); };
  }, [lastMessageId]);

  useEffect(() => {
    if (replyingToId) inputRef.current?.focus();
  }, [replyingToId]);

  useEffect(() => {
    if (!showAttachMenu) return;
    function handleClick(e: MouseEvent) {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target as Node)) setShowAttachMenu(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showAttachMenu]);

  useEffect(() => {
    if (!showChannelInfo) { setChannelInfoShift(0); return; }
    const place = () => {
      const anchor = channelInfoRef.current;
      if (!anchor) return;
      const { left } = anchor.getBoundingClientRect();
      const margin = 12;
      const overflowRight = left + CHANNEL_POPOVER_WIDTH + margin - window.innerWidth;
      // Deborde a droite : on remonte le popover vers la gauche, sans jamais le
      // pousser hors de la fenetre de l'autre cote (panneau tres etroit).
      const shift = overflowRight > 0 ? -Math.min(overflowRight, Math.max(0, left - margin)) : 0;
      setChannelInfoShift(shift);
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [showChannelInfo]);

  // Click outside channel info popover
  useEffect(() => {
    if (!showChannelInfo) return;
    function handleClick(e: MouseEvent) {
      if (channelInfoRef.current && !channelInfoRef.current.contains(e.target as Node)) setShowChannelInfo(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showChannelInfo]);

  const handleSaveWaNumber = async () => {
    if (!discussionId || !waNumberInput.trim()) return;
    setSavingWaNumber(true);
    try {
      await messagerieApi.setWhatsappPhone(discussionId, waNumberInput.trim(), waUpdateContact);
      showNotification({ type: "success", message: "Numéro WhatsApp mis à jour" });
      setEditingWaNumber(false);
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la mise à jour du numéro" });
    } finally {
      setSavingWaNumber(false);
    }
  };

  // La modale (WhatsAppTemplateModal) choisit le template et remplit ses variables ;
  // ici on ne décide que du canal : texte libre si la fenêtre 24h est ouverte, sinon
  // template approuvé via l'API. Retourne true si le message est parti.
  const handleSendTemplate = async (t: WhatsAppTemplate, vars: Record<string, string>): Promise<boolean> => {
    if (!activeDiscussionId) return false;
    setSendingTemplate(true);
    try {
      // facture_request passe toujours par l'API : c'est elle qui pose le drapeau
      // « facture en attente » sur la discussion, et la réponse du client déclenche
      // l'envoi automatique du PDF (fenêtre ouverte : la passerelle envoie en texte).
      if (whatsappWindowOpen && t.name !== "facture_request") {
        // Send as regular message with resolved body
        let resolved = t.body;
        for (const [k, v] of Object.entries(vars)) {
          resolved = resolved.split(`{{${k}}}`).join(v);
        }
        const channel = chatTab === "client" ? (channelTab === "whatsapp" && isWhatsappLinked ? "whatsapp" : channelTab === "email" ? "email" : undefined) : undefined;
        sendMessageTo(activeDiscussionId, resolved, null, undefined, undefined, undefined, channel);
        markFollowingOnSend();
      } else {
        await whatsappApi.sendTemplate({
          discussion_id: activeDiscussionId,
          template_id: t.id,
          variable_values: vars,
        });
      }
      return true;
    } catch (err: any) {
      showNotification({
        type: "error",
        message: err?.response?.data?.detail || "Erreur d'envoi du template",
      });
      return false;
    } finally {
      setSendingTemplate(false);
    }
  };

  const [composerPicker, setComposerPicker] = useState<{ x: number; y: number } | null>(null);

  // Insert at the caret rather than appending, so an emoji lands where the
  // user is typing.
  const insertEmojiAtCaret = (emoji: string) => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? inputText.length;
    const end = el?.selectionEnd ?? start;
    const next = inputText.slice(0, start) + emoji + inputText.slice(end);
    setInputText(next);
    requestAnimationFrame(() => {
      el?.focus();
      const caret = start + emoji.length;
      el?.setSelectionRange(caret, caret);
    });
  };


  const [sendingConsent, setSendingConsent] = useState(false);
  const sendViaConsent = useCallback(async (text: string) => {
    if (!activeDiscussionId || !text || sendingConsent) return;
    setSendingConsent(true);
    try {
      const res = await whatsappApi.sendWithConsent({ discussion_id: activeDiscussionId, text });
      markFollowingOnSend();
      setInputText("");
      setReplyingToId(null);
      setMentionIds([]);
      setMentionQuery(null);
      setTrackedFolders([]);
      setFolderQuery(null);
      if (inputRef.current) inputRef.current.style.height = "auto";
      showNotification({
        type: "success",
        message: res?.request_message
          ? "Demande d'accord envoyée : votre message partira dès que le client répondra"
          : "Message ajouté à la file : il partira dès que le client répondra",
      });
    } catch (err: any) {
      showNotification({ type: "error", message: err?.response?.data?.detail || "Envoi via demande d'accord impossible" });
    } finally {
      setSendingConsent(false);
    }
  }, [activeDiscussionId, sendingConsent, markFollowingOnSend]);

  const handleSend = useCallback(() => {
    const text = inputText.trim();
    if (!text && !stagedFile) return;
    if (!activeDiscussionId) return;
    // Guards the Enter-key path too, not just the disabled button.
    if (waChannelBlocked) {
      showNotification({
        type: "error",
        message: waBlock.reason === "disabled"
          ? "L'envoi WhatsApp est désactivé dans les réglages"
          : "WhatsApp est déconnecté — reconnectez la session avant d'envoyer",
      });
      return;
    }
    if (clientMuteBlocked) {
      showNotification({
        type: "error",
        message: "Client en sourdine — réactivez ses notifications pour lui écrire",
      });
      return;
    }
    if (waWindowBlocked) {
      // Fenêtre fermée : le texte part via la demande d'accord (file serveur).
      if (stagedFile) {
        showNotification({
          type: "error",
          message: "Fenêtre de 24h fermée : une pièce jointe ne peut pas partir via demande d'accord — envoyez le texte seul ou un template",
        });
        return;
      }
      void sendViaConsent(text);
      return;
    }

    // When on WhatsApp channel tab and discussion is linked, send via WhatsApp
    const channel = chatTab === "client" ? (channelTab === "whatsapp" && isWhatsappLinked ? "whatsapp" : channelTab === "email" ? "email" : undefined) : undefined;
    const mentions = mentionIds.length > 0 ? mentionIds : undefined;
    const attachment = stagedFile ? { name: stagedFile.file.name, type: stagedFile.type } as const : undefined;
    const file = stagedFile?.file;
    // Only forward folder refs whose token actually survived in the text.
    const usedFolders = trackedFolders.filter((f) => text.includes(`/${f.name}`));
    const opts = usedFolders.length > 0 ? { folderRefs: usedFolders.map((f) => ({ id: f.id, name: f.name })) } : undefined;
    sendMessageTo(activeDiscussionId, text, replyingToId, attachment, mentions, file, channel, opts);
    markFollowingOnSend();
    setInputText("");
    setStagedFile(null);
    setReplyingToId(null);
    setMentionIds([]);
    setMentionQuery(null);
    setTrackedFolders([]);
    setFolderQuery(null);
    if (inputRef.current) inputRef.current.style.height = "auto";
    // Clear typing indicator immediately on send
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    clearTyping(activeDiscussionId);
  }, [inputText, stagedFile, activeDiscussionId, replyingToId, sendMessageTo, chatTab, channelTab, isWhatsappLinked, waChannelBlocked, waWindowBlocked, clientMuteBlocked, mentionIds, trackedFolders, clearTyping, markFollowingOnSend, sendViaConsent]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInputText(val);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
    if (val.trim()) handleTypingSignal();

    // Detect @mention trigger
    const cursor = e.target.selectionStart ?? val.length;
    const textBefore = val.slice(0, cursor);
    const atMatch = textBefore.match(/@([^\s@]*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }

    // Detect "/" folder trigger (only after whitespace / at start).
    const slashMatch = textBefore.match(/(?:^|\s)\/([^\s/]*)$/);
    if (slashMatch) {
      setFolderQuery(slashMatch[1]);
      setFolderIndex(0);
    } else {
      setFolderQuery(null);
    }
  };

  const insertFolder = useCallback((ref: FolderRef) => {
    const textarea = inputRef.current;
    if (!textarea) return;
    const cursor = textarea.selectionStart ?? inputText.length;
    const textBefore = inputText.slice(0, cursor);
    const slashIdx = textBefore.lastIndexOf("/");
    if (slashIdx === -1) return;
    const before = inputText.slice(0, slashIdx);
    const after = inputText.slice(cursor);
    const newText = `${before}/${ref.name} ${after}`;
    setInputText(newText);
    setTrackedFolders((prev) => prev.some((f) => f.id === ref.id) ? prev : [...prev, ref]);
    setFolderQuery(null);
    requestAnimationFrame(() => {
      const pos = slashIdx + ref.name.length + 2; // "/Name "
      textarea.setSelectionRange(pos, pos);
      textarea.focus();
    });
  }, [inputText]);

  const insertMention = useCallback((member: { id: string; name: string }) => {
    const textarea = inputRef.current;
    if (!textarea) return;
    const cursor = textarea.selectionStart ?? inputText.length;
    const textBefore = inputText.slice(0, cursor);
    const atIdx = textBefore.lastIndexOf("@");
    if (atIdx === -1) return;

    const before = inputText.slice(0, atIdx);
    const after = inputText.slice(cursor);
    const newText = `${before}@${member.name} ${after}`;
    setInputText(newText);
    setMentionIds((prev) => prev.includes(member.id) ? prev : [...prev, member.id]);
    setMentionQuery(null);

    // Restore cursor after the inserted mention
    requestAnimationFrame(() => {
      const pos = atIdx + member.name.length + 2; // @Name + space
      textarea.setSelectionRange(pos, pos);
      textarea.focus();
    });
  }, [inputText]);

  const handleReact = useCallback((msgId: string, emoji: string) => {
    if (!activeDiscussionId) return;
    toggleReactionIn(activeDiscussionId, msgId, emoji);
  }, [activeDiscussionId, toggleReactionIn]);

  const handleDelete = useCallback((msgId: string) => {
    if (!activeDiscussionId) return;
    deleteMessageIn(activeDiscussionId, msgId);
  }, [activeDiscussionId, deleteMessageIn]);

  // Stable edit handler so ChatBubble's memo isn't defeated by a fresh closure.
  const handleEdit = useCallback((msgId: string, newText: string) => {
    if (!activeDiscussionId) return;
    updateMessageIn(activeDiscussionId, msgId, newText);
  }, [activeDiscussionId, updateMessageIn]);

  // Re-send a WhatsApp message that failed to deliver (e.g. session was disconnected).
  const handleRetry = useCallback(async (msgId: string) => {
    if (!activeDiscussionId) return;
    try {
      await messagerieApi.retryMessage(activeDiscussionId, msgId);
    } catch (e: any) {
      // Fenêtre de 24 h fermée, limite d'envoi… : le motif du serveur, pas un échec muet.
      const detail = e?.response?.data?.detail;
      showNotification({ type: "error", message: typeof detail === "string" && detail ? detail : "Échec du renvoi WhatsApp" });
    } finally {
      // Refresh so the bubble reflects sent/failed (backend updated the doc either way).
      fetchMessages(activeDiscussionId);
    }
  }, [activeDiscussionId, fetchMessages]);

  const handleFileAttach = useCallback((file: File, type: "image" | "file") => {
    if (!activeDiscussionId) return;
    const isEmailChannel = chatTab === "client" && channelTab === "email";
    if (isEmailChannel) {
      // Stage file so user can add text before sending
      const previewUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined;
      setStagedFile({ file, type, previewUrl });
      setShowAttachMenu(false);
      inputRef.current?.focus();
      return;
    }
    const attachment = { name: file.name, type } as const;
    const channel = chatTab === "client" ? (channelTab === "whatsapp" && isWhatsappLinked ? "whatsapp" : undefined) : undefined;
    sendMessageTo(activeDiscussionId, "", replyingToId, attachment, undefined, file, channel);
    markFollowingOnSend();
    setReplyingToId(null);
    setShowAttachMenu(false);
  }, [activeDiscussionId, replyingToId, sendMessageTo, chatTab, channelTab, isWhatsappLinked, markFollowingOnSend]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/ogg;codecs=opus") ? "audio/ogg;codecs=opus"
        : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      recordingChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) recordingChunksRef.current.push(e.data); };
      recorder.start(250);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch {
      // Mic permission denied or unavailable
    }
  }, []);

  const stopRecording = useCallback((send: boolean) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
    recorder.onstop = () => {
      recorder.stream.getTracks().forEach((t) => t.stop());
      if (send && recordingChunksRef.current.length > 0 && activeDiscussionId) {
        const ext = recorder.mimeType.includes("ogg") ? "ogg" : "webm";
        const blob = new Blob(recordingChunksRef.current, { type: recorder.mimeType });
        const file = new File([blob], `audio_${Date.now()}.${ext}`, { type: recorder.mimeType });
        const attachment = { name: file.name, type: "file" as const };
        const channel = chatTab === "client" ? (channelTab === "whatsapp" && isWhatsappLinked ? "whatsapp" : channelTab === "email" ? "email" : undefined) : undefined;
        sendMessageTo(activeDiscussionId, "", replyingToId, attachment, undefined, file, channel);
        markFollowingOnSend();
        setReplyingToId(null);
      }
      recordingChunksRef.current = [];
    };
    recorder.stop();
    mediaRecorderRef.current = null;
    setIsRecording(false);
    setRecordingTime(0);
  }, [activeDiscussionId, replyingToId, sendMessageTo, chatTab, channelTab, isWhatsappLinked, markFollowingOnSend]);

  // Scroll-up detection for loading older messages
  const handleMessagesScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el || !activeDiscussionId) return;
    if (el.scrollTop < 100 && hasMoreMessages[activeDiscussionId] && !isLoadingOlderMessages) {
      const prevScrollHeight = el.scrollHeight;
      fetchOlderMessages(activeDiscussionId).then(() => {
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight - prevScrollHeight;
          }
        });
      });
    }
  }, [activeDiscussionId, hasMoreMessages, isLoadingOlderMessages, fetchOlderMessages]);

  const replyMessage = replyingToId ? messages.find((m) => m.id === replyingToId) : undefined;
  const groups = useMemo(() => groupChatMessages(messages), [messages]);

  // In-conversation search (backend-powered)
  const [searchResults, setSearchResults] = useState<import("types/messaging").ChatMessage[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const q = searchQuery.trim();
    if (!q || !activeDiscussionId) {
      setSearchResults([]);
      setSearchCurrentIndex(0);
      return;
    }
    setIsSearching(true);
    searchDebounceRef.current = setTimeout(() => {
      messagerieApi.searchMessages(activeDiscussionId, q)
        .then((results) => { setSearchResults(results); setSearchCurrentIndex(0); })
        .catch(() => setSearchResults([]))
        .finally(() => setIsSearching(false));
    }, 400);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchQuery, activeDiscussionId]);

  const searchMatchIds = searchResults.map((m) => m.id);
  const activeSearchMsgId = searchMatchIds.length > 0 ? searchMatchIds[searchCurrentIndex] : null;

  // Scroll to active search match — load older messages if needed
  const [isLoadingToMatch, setIsLoadingToMatch] = useState(false);
  useEffect(() => {
    if (!activeSearchMsgId || !activeDiscussionId) return;
    let cancelled = false;

    const scrollToMatch = async () => {
      // Check if already in DOM
      const el = document.getElementById(`chat-msg-${activeSearchMsgId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      // Message not loaded — fetch older batches until it appears
      setIsLoadingToMatch(true);
      const store = useMessagerieStore.getState;
      let attempts = 0;
      const maxAttempts = 40; // 40 * 50 = 2000 messages max

      while (!cancelled && attempts < maxAttempts) {
        const { hasMoreMessages, isLoadingOlderMessages } = store();
        if (!hasMoreMessages[activeDiscussionId]) break;
        if (isLoadingOlderMessages) {
          await new Promise((r) => setTimeout(r, 100));
          continue;
        }

        await useMessagerieStore.getState().fetchOlderMessages(activeDiscussionId);
        attempts++;

        // Check if message is now loaded
        const loaded = (store().messages[activeDiscussionId] || []).some((m) => m.id === activeSearchMsgId);
        if (loaded) break;
      }

      if (!cancelled) {
        setIsLoadingToMatch(false);
        // Wait for DOM to render the new messages
        requestAnimationFrame(() => {
          const target = document.getElementById(`chat-msg-${activeSearchMsgId}`);
          if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      }
    };

    scrollToMatch();
    return () => { cancelled = true; };
  }, [activeSearchMsgId, activeDiscussionId]);

  const noDiscussion = !activeDiscussionId;

  return (
    <div ref={rootRef} className="flex flex-row h-full">
    <div className="flex flex-col h-full flex-1 min-w-0 border-l" style={{ background: isDark ? "#1E1E2E" : "#FAFAFA", borderColor: isDark ? "#333348" : undefined }}>
      {/* Chat/Private tabs */}
      <div className="px-4 pt-4" style={{ background: isDark ? "#1E1E2E" : "#FFFFFF", borderBottom: `1px solid ${isDark ? "#333348" : "#E5E5E5"}` }}>
        {/* De qui parle-t-on ? Le fil ouvert n'affichait que les onglets
            Client/Interne : le numéro d'OR et le nom du client, visibles dans la
            liste, disparaissaient dès qu'on cliquait dessus. */}
        {entityName && (
          <div
            className="text-h1 font-semibold truncate pb-2"
            style={{ fontFamily: "Poppins", color: isDark ? "#F3F4F6" : "#000000" }}
            title={entityName}
          >
            {entityName}
          </div>
        )}
        {/* Barre d'outils du fil : qui (Client/Interne), par quel canal, et le suivi.
            Les trois vivaient sur trois lignes empilees ; dans la modale Ordre le chat
            n'a presque plus de hauteur et cet en-tete en mangeait la moitie. `flex-wrap`
            rend les lignes aux hotes etroits (widget flottant, panneau lateral). */}
        <div className="flex flex-row items-center flex-wrap pt-3 pb-3" style={{ gap: 8 }}>
          {/* Client / Interne : un segmented control inline, la ou ChunkyTabs occupait
              une ligne pleine largeur a lui seul. */}
          <div
            className="flex flex-row items-center flex-shrink-0 rounded-full"
            style={{
              padding: 2,
              gap: 2,
              background: isDark ? "rgba(255,255,255,0.08)" : "#F3F4F6",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "#E5E7EB"}`,
            }}
          >
            {[
              { value: "client" as const, label: "Client", badge: clientUnread, badgeColor: "#6366f1" },
              { value: "privee" as const, label: "Interne", badge: interneUnread, badgeColor: "#525252" },
            ].map((tab) => {
              const active = chatTab === tab.value;
              return (
                <button
                  key={tab.value}
                  onClick={() => { setChatTab(tab.value); setReplyingToId(null); setSearchQuery(""); setSearchResults([]); setSearchCurrentIndex(0); }}
                  className="relative rounded-full transition-colors"
                  style={{
                    padding: "3px 12px",
                    fontFamily: "Poppins",
                    fontSize: 11,
                    lineHeight: "16px",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    background: active ? (isDark ? "rgba(255,255,255,0.18)" : "#FFFFFF") : "transparent",
                    color: active ? (isDark ? "#F3F4F6" : "#111827") : (isDark ? "#9CA3AF" : "#6B7280"),
                    boxShadow: active && !isDark ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                  }}
                  title={tab.value === "client" ? "Fil avec le client" : "Fil interne a l'equipe"}
                >
                  {tab.label}
                  {!active && tab.badge != null && tab.badge > 0 && (
                    <span
                      className="absolute"
                      style={{ top: 1, right: 3, width: 6, height: 6, borderRadius: 999, background: tab.badgeColor }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {!isDark ? (
            <div className="flex flex-row items-center gap-1.5 relative" ref={channelInfoRef}>
              {/* Active channel pill (click to open channel switcher) */}
              <button
                onClick={() => setShowChannelInfo((v) => !v)}
                className="flex flex-row items-center gap-1.5 px-3 py-1 rounded-full text-h4 font-medium border transition-colors"
                style={{
                  background: channelTab === "whatsapp"
                    ? (isWhatsappLinked && !whatsappWindowOpen ? "#F9FAFB" : "#F0FDF4")
                    : "#F0FDF4",
                  borderColor: channelTab === "whatsapp"
                    ? (isWhatsappLinked && !whatsappWindowOpen ? "#D1D5DB" : "#86EFAC")
                    : "#86EFAC",
                  color: channelTab === "whatsapp"
                    ? (isWhatsappLinked && !whatsappWindowOpen ? "#374151" : "#166534")
                    : "#166534",
                  cursor: "pointer",
                }}
              >
                {channelTab === "whatsapp" ? (
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="#25D366">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                ) : (
                  <Icon1 name="email" size={14} color="#22c55e" />
                )}
                <span>{channelTab === "whatsapp" ? "WhatsApp" : "Email"}</span>
                {/* À qui ça part : le destinataire n'était visible qu'en ouvrant le
                    popover, alors que c'est la première chose qu'on veut vérifier
                    avant d'écrire (surtout quand le numéro WhatsApp a été saisi à la
                    main et diffère du contact principal). */}
                {channelRecipient && (
                  <span
                    className="truncate"
                    style={{ maxWidth: 150, fontWeight: 400, opacity: 0.85 }}
                    title={channelRecipientRaw}
                  >
                    {channelRecipient}
                  </span>
                )}
                {channelTab === "whatsapp" && isWhatsappLinked && (
                  <>
                    <div style={{ width: 1, height: 12, background: whatsappWindowOpen ? "#86EFAC" : "#D1D5DB" }} />
                    <div className="flex items-center gap-1">
                      <div style={{ width: 6, height: 6, borderRadius: 999, background: whatsappWindowOpen ? "#25D366" : "#6B7280" }} />
                      <span style={{ fontSize: 10, color: whatsappWindowOpen ? "#166534" : "#374151" }}>
                        {whatsappWindowOpen ? `${whatsappWindowHours}h` : "templates"}
                      </span>
                    </div>
                  </>
                )}
                <svg width={10} height={10} viewBox="0 0 10 10" fill="none" style={{ marginLeft: 2 }}>
                  <path d={showChannelInfo ? "M8 7L5 4L2 7" : "M2 3L5 6L8 3"} stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {/* Channel info popover */}
              {showChannelInfo && (
                <div
                  className="absolute z-50 rounded-xl border shadow-lg"
                  style={{ top: "calc(100% + 8px)", left: channelInfoShift, width: CHANNEL_POPOVER_WIDTH, maxWidth: "calc(100vw - 24px)", background: "#FFFFFF", borderColor: "#E5E7EB" }}
                >
                  <div className="p-4 flex flex-col gap-3">
                    <div className="flex flex-row items-center justify-between">
                      <span className="text-h3 font-semibold" style={{ color: "#171717" }}>Canal de communication</span>
                      <button onClick={() => setShowChannelInfo(false)} className="hover:bg-neutral-100 rounded-full p-1">
                        <svg width={10} height={10} viewBox="0 0 12 12" fill="none">
                          <path d="M1 1L11 11M11 1L1 11" stroke="#A1A1A1" strokeWidth={1.5} strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>

                    {/* WhatsApp option */}
                    <button
                      onClick={() => { setChannelOverride("whatsapp"); }}
                      className="flex flex-row items-start gap-3 p-3 rounded-lg border transition-colors text-left"
                      style={{
                        borderColor: channelTab === "whatsapp" ? "#86EFAC" : "#E5E7EB",
                        background: channelTab === "whatsapp" ? "#F0FDF4" : "#FAFAFA",
                      }}
                    >
                      <svg width={20} height={20} viewBox="0 0 24 24" fill="#25D366" className="flex-shrink-0 mt-0.5">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      <div className="flex flex-col gap-1">
                        <span className="text-h3 font-medium" style={{ color: "#171717" }}>WhatsApp</span>
                        <span className="text-h4" style={{ color: "#737373", lineHeight: "1.4" }}>
                          Messages instantanés via WhatsApp. Fenêtre de 24h après le dernier message du client pour envoyer des messages libres. Hors fenêtre, seuls les templates sont disponibles.
                        </span>
                        {isWhatsappLinked && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <div style={{ width: 6, height: 6, borderRadius: 999, background: whatsappWindowOpen ? "#25D366" : "#6B7280" }} />
                            <span className="text-h4" style={{ color: whatsappWindowOpen ? "#166534" : "#6B7280" }}>
                              {whatsappWindowOpen ? `Fenêtre ouverte (${whatsappWindowHours}h restantes)` : "Templates uniquement"}
                            </span>
                          </div>
                        )}
                        {!isWhatsappLinked && (
                          <span className="text-h4 mt-1" style={{ color: "#EF4444" }}>Non lié</span>
                        )}
                        <div className="flex flex-row items-center gap-2 mt-1">
                          <span className="text-h5" style={{ color: "#A1A1A1" }}>
                            {whatsappWindowOpen ? "Gratuit (fenêtre ouverte)" : "~0,05-0,15 EUR / template"}
                          </span>
                        </div>
                      </div>
                    </button>

                    {/* WhatsApp recipient number — view / edit */}
                    <div className="flex flex-col gap-2 px-3 py-2 rounded-lg border" style={{ borderColor: "#E5E7EB", background: "#FAFAFA" }}>
                      {!editingWaNumber ? (
                        <div className="flex flex-row items-center justify-between gap-2">
                          <div className="flex flex-col">
                            <span className="text-h5" style={{ color: "#A1A1A1" }}>Numéro WhatsApp</span>
                            <span className="text-h4 font-medium" style={{ color: storeDiscussion?.whatsapp_phone ? "#171717" : "#A1A1A1" }}>
                              {storeDiscussion?.whatsapp_phone || "Non défini (utilise le contact principal)"}
                            </span>
                          </div>
                          <button
                            onClick={() => { setWaNumberInput(storeDiscussion?.whatsapp_phone || ""); setWaUpdateContact(false); setEditingWaNumber(true); }}
                            className="text-h4 font-medium px-2 py-1 rounded-lg hover:bg-neutral-100 transition-colors flex-shrink-0"
                            style={{ color: "#6366f1" }}
                          >
                            Modifier
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2">
                          <span className="text-h5" style={{ color: "#A1A1A1" }}>Numéro WhatsApp destinataire</span>
                          <input
                            type="tel"
                            value={waNumberInput}
                            onChange={(e) => setWaNumberInput(e.target.value)}
                            placeholder="06 12 34 56 78 ou +33 6 12 34 56 78"
                            className="w-full px-3 py-2 rounded-lg border text-h4 outline-none focus:border-indigo-400"
                            style={{ borderColor: "#E5E7EB" }}
                            autoFocus
                          />
                          <label className="flex flex-row items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={waUpdateContact} onChange={(e) => setWaUpdateContact(e.target.checked)} />
                            <span className="text-h4" style={{ color: "#525252" }}>Mettre à jour aussi le contact principal du dossier</span>
                          </label>
                          <div className="flex flex-row justify-end gap-2">
                            <button onClick={() => setEditingWaNumber(false)} disabled={savingWaNumber} className="text-h4 px-3 py-1.5 rounded-lg hover:bg-neutral-100" style={{ color: "#737373" }}>Annuler</button>
                            <button onClick={handleSaveWaNumber} disabled={savingWaNumber || !waNumberInput.trim()} className="text-h4 font-medium px-3 py-1.5 rounded-lg text-white disabled:opacity-50" style={{ background: "#6366f1" }}>
                              {savingWaNumber ? "Enregistrement..." : "Enregistrer"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Email option */}
                    <button
                      onClick={() => { setChannelOverride("email"); }}
                      className="flex flex-row items-start gap-3 p-3 rounded-lg border transition-colors text-left"
                      style={{
                        borderColor: channelTab === "email" ? "#86EFAC" : "#E5E7EB",
                        background: channelTab === "email" ? "#F0FDF4" : "#FAFAFA",
                      }}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        <Icon1 name="email" size={20} color="#6B7280" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-h3 font-medium" style={{ color: "#171717" }}>Email</span>
                        <span className="text-h4" style={{ color: "#737373", lineHeight: "1.4" }}>
                          Emails classiques. Pas de limite de temps, mais délais de réponse plus longs.
                        </span>
                        {isEmailLinked ? (
                          <div className="flex items-center gap-1.5 mt-1">
                            <div style={{ width: 6, height: 6, borderRadius: 999, background: "#25D366" }} />
                            <span className="text-h4" style={{ color: "#166534" }}>Lié ({storeDiscussion?.client_email})</span>
                          </div>
                        ) : (
                          <span className="text-h4 mt-1" style={{ color: "#EF4444" }}>Non lié</span>
                        )}
                        <div className="flex flex-row items-center gap-2 mt-1">
                          <span className="text-h5" style={{ color: "#A1A1A1" }}>~0,01 EUR / email (Resend)</span>
                        </div>
                      </div>
                    </button>

                    {/* Booking confirmation info */}
                    {clientChannel && (
                      <div className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "#F5F5F5" }}>
                        <Icon1 name="info" size={14} color="#A1A1A1" />
                        <span className="text-h4" style={{ color: "#737373" }}>
                          Client a confirmé via <span className="font-medium" style={{ color: "#525252" }}>{clientChannel === "whatsapp" ? "WhatsApp" : "Email"}</span>
                        </span>
                      </div>
                    )}

                    {/* Sourdine complète du client (dossier) — vaut pour tous ses ordres */}
                    <div
                      className="flex flex-row items-start gap-3 px-3 py-2 rounded-lg border"
                      style={{
                        borderColor: clientMuted ? "#FCD34D" : "#E5E7EB",
                        background: clientMuted ? "#FFFBEB" : "#FAFAFA",
                      }}
                    >
                      <div className="flex flex-col gap-1 flex-1">
                        <span className="text-h4 font-medium" style={{ color: "#171717" }}>Notifications client</span>
                        <span className="text-h5" style={{ color: "#737373", lineHeight: "1.4" }}>
                          {clientMuted
                            ? "En sourdine : aucun email, WhatsApp ou SMS ne part vers ce client, ni automatiquement ni manuellement."
                            : "Actives : le client reçoit confirmations, rappels et messages."}
                        </span>
                        {clientMuted && clientMute?.muted_by_name && (
                          <span className="text-h5" style={{ color: "#92400E" }} title={muteTooltip(clientMute)}>Coupé par {clientMute.muted_by_name}</span>
                        )}
                      </div>
                      <button
                        onClick={() => toggleClientMute(!clientMuted)}
                        disabled={clientMuteBusy || !clientMute?.dossier_id}
                        className="text-h4 font-medium px-2 py-1 rounded-lg hover:bg-neutral-100 transition-colors flex-shrink-0 disabled:opacity-40"
                        style={{ color: clientMuted ? "#92400E" : "#6366f1" }}
                      >
                        {clientMuted ? "Réactiver" : "Mettre en sourdine"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
              Interne
            </span>
          )}

          {isEntity && discussionId && (
            <button
              onClick={async () => {
                if (followLoading) return;
                setFollowLoading(true);
                try {
                  if (isFollowing) {
                    await unfollowDiscussion(discussionId);
                    setLocalFollowState(false);
                  } else {
                    await followDiscussion(discussionId);
                    setLocalFollowState(true);
                  }
                } catch {
                  showNotification({ type: "error", message: "Erreur lors du suivi" });
                } finally {
                  setFollowLoading(false);
                }
              }}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-h4 font-medium border transition-colors"
              style={{
                background: isDark
                  ? (isFollowing ? "rgba(165,180,252,0.15)" : "rgba(255,255,255,0.08)")
                  : (isFollowing ? "#EEF2FF" : "#F9FAFB"),
                borderColor: isDark
                  ? (isFollowing ? "rgba(165,180,252,0.4)" : "rgba(255,255,255,0.15)")
                  : (isFollowing ? "#A5B4FC" : "#E5E7EB"),
                color: isDark
                  ? (isFollowing ? "#C7D2FE" : "#9CA3AF")
                  : (isFollowing ? "#4338CA" : "#6B7280"),
                opacity: followLoading ? 0.6 : 1,
                cursor: followLoading ? "wait" : "pointer",
                fontSize: 11,
                lineHeight: "16px",
              }}
              title={isFollowing ? "Ne plus suivre cet ordre" : "Suivre cet ordre"}
            >
              <Icon1 name="cloche" size={12} color={isDark ? (isFollowing ? "#C7D2FE" : "#6B7280") : (isFollowing ? "#4338CA" : "#9CA3AF")} />
              {isFollowing ? "Notifications actives" : "Recevoir les notifications"}
            </button>
          )}

          <div className="flex flex-row items-center ml-auto" style={{ gap: 4 }}>
            {entityLinks?.ordreId && (
              <button
                onClick={() => navigateToFolderRef({ id: entityLinks.ordreId!, kind: "ordre" })}
                className={`rounded-md flex items-center transition-colors ${isDark ? "hover:bg-white/10" : "hover:bg-neutral-100"}`}
                style={{ height: 28, padding: "0 8px", gap: 4 }}
                title="Ouvrir l'ordre"
              >
                <span className="text-h4 font-medium" style={{ fontFamily: "Poppins", color: isDark ? "#D1D5DB" : "#6B7280" }}>Ordre</span>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={isDark ? "#D1D5DB" : "#6B7280"} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7M8 7h9v9" /></svg>
              </button>
            )}
            {entityLinks?.dossierId && (
              <button
                onClick={() => navigateToFolderRef({ id: entityLinks.dossierId!, kind: "dossier" })}
                className={`rounded-md flex items-center transition-colors ${isDark ? "hover:bg-white/10" : "hover:bg-neutral-100"}`}
                style={{ height: 28, padding: "0 8px", gap: 4 }}
                title="Ouvrir le dossier"
              >
                <span className="text-h4 font-medium" style={{ fontFamily: "Poppins", color: isDark ? "#D1D5DB" : "#6B7280" }}>Dossier</span>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={isDark ? "#D1D5DB" : "#6B7280"} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7M8 7h9v9" /></svg>
              </button>
            )}
            {!isDark && isEmailLinked && (
              <button
                onClick={async () => {
                  try {
                    const res = await messagerieApi.checkEmailsNow();
                    if (res.matched > 0) {
                      showNotification({ type: "success", message: `${res.matched} email(s) récupéré(s)` });
                    } else {
                      showNotification({ type: "default", message: "Aucun nouvel email" });
                    }
                  } catch {
                    showNotification({ type: "error", message: "Erreur lors de la vérification" });
                  }
                }}
                className="rounded-md flex items-center justify-center transition-colors hover:bg-neutral-100"
                style={{ width: 28, height: 28 }}
                title="Vérifier les emails maintenant"
              >
                <Icon1 name="sync" size={14} color="#A1A1A1" />
              </button>
            )}
            {devOrg && activeDiscussionId && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(activeDiscussionId);
                }}
                className="text-h4 text-neutral-300 hover:text-neutral-500 transition-colors"
                title={activeDiscussionId}
              >
                <Icon1 name="copy" size={12} color="#D4D4D4" />
              </button>
            )}
            <button
              onClick={() => { setSearchOpen((v) => !v); if (!searchOpen) setTimeout(() => searchInputRef.current?.focus(), 50); if (searchOpen) { setSearchQuery(""); setSearchCurrentIndex(0); } }}
              className={`rounded-md flex items-center justify-center transition-colors ${isDark ? "hover:bg-white/10" : "hover:bg-neutral-100"}`}
              style={{ width: 28, height: 28 }}
              title="Rechercher"
            >
              <Icon1 name="search" size={14} color={searchOpen ? (isDark ? "#FFFFFF" : "#000000") : (isDark ? "#6B7280" : "#A1A1A1")} />
            </button>
          </div>

          {resolvedMembers.length > 0 && (
            <button
              type="button"
              onClick={onToggleInfo || (() => setShowInfoPanel((v) => !v))}
              className="flex flex-row items-center cursor-pointer hover:opacity-80 transition-opacity"
              style={{ gap: 8, background: "none", border: "none", padding: 0 }}
            >
              <div className="flex flex-row" style={{ marginLeft: -4 }}>
                {resolvedMembers.slice(0, 5).map((m, i) => (
                  <div key={m.name} className="flex-shrink-0 overflow-hidden" style={{ borderRadius: 999, marginLeft: i > 0 ? -8 : 0, border: isDark ? "2px solid #1E1E2E" : "2px solid #FFFFFF" }}>
                    <UserAvatar src={m.photo_url} value={m.avatar_seed || m.name} size={24} className="w-6 h-6 rounded-full" />
                  </div>
                ))}
                {resolvedMembers.length > 5 && (
                  <div
                    className="flex-shrink-0 flex items-center justify-center"
                    style={{
                      borderRadius: 999,
                      marginLeft: -8,
                      border: isDark ? "2px solid #1E1E2E" : "2px solid #FFFFFF",
                      width: 24,
                      height: 24,
                      background: isDark ? "#374151" : "#E5E7EB",
                    }}
                  >
                    <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 10, color: isDark ? "#D1D5DB" : "#6B7280" }}>
                      +{resolvedMembers.length - 5}
                    </span>
                  </div>
                )}
              </div>
              {resolvedOnlineCount > 0 && (
                <div className="flex flex-row items-center" style={{ gap: 5 }}>
                  <div style={{ width: 6, height: 6, borderRadius: 999, background: "#5EEAD4" }} />
                  <span className="text-h3" style={{ fontFamily: "Poppins", fontWeight: 500, color: "#5EEAD4" }}>
                    {resolvedOnlineCount} en ligne
                  </span>
                </div>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Search bar */}
      {searchOpen && (
        <div
          className="flex flex-row items-center"
          style={{
            padding: "8px 12px",
            gap: 8,
            background: isDark ? "#2D2D3D" : "#FFFFFF",
            borderBottom: `1px solid ${isDark ? "#333348" : "#E5E5E5"}`,
          }}
        >
          <Icon1 name="search" size={14} color={isDark ? "#6B7280" : "#A1A1A1"} />
          <input
            ref={searchInputRef}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setSearchCurrentIndex(0); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && searchMatchIds.length > 0) {
                if (e.shiftKey) {
                  setSearchCurrentIndex((i) => (i - 1 + searchMatchIds.length) % searchMatchIds.length);
                } else {
                  setSearchCurrentIndex((i) => (i + 1) % searchMatchIds.length);
                }
              }
              if (e.key === "Escape") { setSearchOpen(false); setSearchQuery(""); setSearchCurrentIndex(0); }
            }}
            placeholder="Rechercher dans la conversation..."
            className={`flex-1 outline-none border-none text-h3 bg-transparent ${isDark ? "placeholder:text-[#6B7280]" : "placeholder:text-neutral-400"}`}
            style={{ fontFamily: "Poppins", fontWeight: 400, color: isDark ? "#FFFFFF" : "#000000" }}
          />
          {searchQuery.trim() && (
            <span className="text-h4 flex-shrink-0" style={{ fontFamily: "Poppins", fontWeight: 500, color: isDark ? "#A1A1A1" : "#6B7280" }}>
              {isSearching ? "..." : searchMatchIds.length > 0 ? `${searchCurrentIndex + 1}/${searchMatchIds.length}` : "0/0"}
            </span>
          )}
          {searchMatchIds.length > 0 && (
            <>
              <button
                onClick={() => setSearchCurrentIndex((i) => (i - 1 + searchMatchIds.length) % searchMatchIds.length)}
                className={`rounded-md flex items-center justify-center ${isDark ? "hover:bg-white/10" : "hover:bg-neutral-100"}`}
                style={{ width: 24, height: 24 }}
                title="Résultat précédent"
              >
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                  <path d="M9 5L5 1L1 5" stroke={isDark ? "#D4D4D4" : "#525252"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button
                onClick={() => setSearchCurrentIndex((i) => (i + 1) % searchMatchIds.length)}
                className={`rounded-md flex items-center justify-center ${isDark ? "hover:bg-white/10" : "hover:bg-neutral-100"}`}
                style={{ width: 24, height: 24 }}
                title="Résultat suivant"
              >
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                  <path d="M1 1L5 5L9 1" stroke={isDark ? "#D4D4D4" : "#525252"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </>
          )}
          <button
            onClick={() => { setSearchOpen(false); setSearchQuery(""); setSearchCurrentIndex(0); }}
            className={`rounded-md flex items-center justify-center ${isDark ? "hover:bg-white/10" : "hover:bg-neutral-100"}`}
            style={{ width: 24, height: 24 }}
          >
            <Icon1 name="close" size={10} color={isDark ? "#6B7280" : "#A1A1A1"} />
          </button>
        </div>
      )}

      {/* Pinned messages bar */}
      {!noDiscussion && pinnedMessages.length > 0 && (
        <PinnedBar
          pins={pinnedMessages}
          dark={isDark}
          onJump={jumpToMessage}
          onUnpin={handleUnpin}
        />
      )}

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        onScroll={handleMessagesScroll}
        className="flex-1 overflow-y-auto flex flex-col"
        style={{ gap: 16, padding: "16px 16px 8px", ...(compact ? { maxHeight: 400 } : {}) }}
      >
        {noDiscussion && (
          <div className="flex-1 flex items-center justify-center">
            <span className="text-h3 text-neutral-400">Aucune conversation liée</span>
          </div>
        )}
        {!noDiscussion && isLoadingMessages && messages.length === 0 && (
          <div className="flex-1 flex items-center justify-center">
            <span className="text-h3 text-neutral-400">Chargement...</span>
          </div>
        )}
        {(isLoadingOlderMessages || isLoadingToMatch) && (
          <div className="flex justify-center" style={{ padding: 12 }}>
            <span className="text-h3" style={{ fontFamily: "Poppins", color: "#A1A1A1" }}>
              {isLoadingToMatch ? "Chargement des messages..." : "Chargement..."}
            </span>
          </div>
        )}
        {groups.map((group, gi) => {
          const count = group.messages.length;
          const prev = gi > 0 ? groups[gi - 1] : null;
          const dayKey = msgDayKey({ created_at: group.messages[0].createdAt });
          const showDay = !prev || msgDayKey({ created_at: prev.messages[0].createdAt }) !== dayKey;
          return (
            <Fragment key={group.messages[0].id}>
            {showDay && <DaySeparator iso={group.messages[0].createdAt} dark={isDark} />}
            <div
              className="flex flex-row"
              style={{ justifyContent: group.isOwn ? "flex-end" : "flex-start", gap: 8 }}
            >
              {!group.isOwn && (
                <div className="flex-shrink-0" style={{ width: 32, paddingTop: 2 }}>
                  {group.senderType === "bot" ? (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "#E5E5E5" }}>
                      <svg width={16} height={16} viewBox="0 0 24 24" fill="none"><path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7v1a2 2 0 01-2 2h-1v1a2 2 0 01-2 2H8a2 2 0 01-2-2v-1H5a2 2 0 01-2-2v-1a7 7 0 017-7h1V5.73A2 2 0 0112 2zm-2 12a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm4 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" fill="#737373"/></svg>
                    </div>
                  ) : group.senderType === "client" ? (
                    <ClientAvatar size={32} />
                  ) : (
                    <UserAvatar src={group.photoUrl} value={group.avatarSeed || group.senderIdRaw} size={32} className="w-8 h-8 rounded-full" />
                  )}
                </div>
              )}

              <div className="flex flex-col" style={{ alignItems: group.isOwn ? "flex-end" : "flex-start", gap: 3, maxWidth: "75%" }}>
                {!group.isOwn && (
                  <div className="flex flex-row items-center" style={{ gap: 6, marginBottom: 2, marginLeft: 4 }}>
                    <span className="text-h3 font-semibold" style={{ fontFamily: "Poppins", color: isDark ? "#D4D4D4" : "#525252" }}>
                      {group.senderId}
                    </span>
                    {group.senderType === "bot" && (
                      <span
                        className="flex items-center justify-center text-h5 font-medium"
                        style={{ background: "#E5E5E5", color: "#525252", borderRadius: 4, padding: "1px 5px", fontSize: 10 }}
                      >
                        Bot
                      </span>
                    )}
                    {group.senderType === "client" && (
                      <span
                        className="flex items-center justify-center text-h5 font-medium"
                        style={{ background: "#DBEAFE", color: "#3B82F6", borderRadius: 4, padding: "1px 5px", fontSize: 10 }}
                      >
                        Client
                      </span>
                    )}
                    {group.messages[0].channel === "whatsapp" && (
                      <span
                        className="flex items-center justify-center text-h5 font-medium"
                        style={{ background: "#25D366", color: "#FFFFFF", borderRadius: 4, padding: "1px 5px", fontSize: 10 }}
                      >
                        WhatsApp
                      </span>
                    )}
                    {group.messages[0].senderRole && (
                      <>
                        <div style={{ width: 4, height: 4, borderRadius: 999, background: "#D4D4D4" }} />
                        <span className="text-h4" style={{ fontFamily: "Poppins", color: "#A1A1A1" }}>{group.messages[0].senderRole}</span>
                      </>
                    )}
                  </div>
                )}

                {group.messages.map((msg, i) => {
                  const position: "solo" | "first" | "middle" | "last" =
                    count === 1 ? "solo" : i === 0 ? "first" : i === count - 1 ? "last" : "middle";
                  // Pied (heure + « via SMS / WhatsApp ») aussi quand le canal change dans
                  // le groupe : l'invitation SMS, entre deux WhatsApp, passait pour un WhatsApp.
                  const showTimestamp = i === count - 1 || msg.channel !== group.messages[i + 1]?.channel;
                  const replySource = msg.replyToId ? messages.find((m) => m.id === msg.replyToId) : undefined;
                  const isSearchMatch = searchMatchIds.includes(msg.id);
                  const isActiveSearchMatch = msg.id === activeSearchMsgId;
                  const isHighlighted = msg.id === highlightedMsgId;

                  return (
                    <div
                      key={msg.id}
                      id={`chat-msg-${msg.id}`}
                      style={{
                        borderRadius: 12,
                        ...(isActiveSearchMatch || isHighlighted
                          ? { outline: `2px solid ${isDark ? "#6366f1" : "#6366f1"}`, outlineOffset: 2, background: isDark ? "rgba(99,102,241,0.12)" : "rgba(99,102,241,0.08)" }
                          : isSearchMatch
                            ? { background: isDark ? "rgba(99,102,241,0.06)" : "rgba(99,102,241,0.04)" }
                            : {}),
                        transition: "outline 0.2s, background 0.2s",
                      }}
                    >
                    <ChatBubble
                      msg={msg}
                      position={position}
                      showTimestamp={showTimestamp}
                      replySource={replySource}
                      onReact={handleReact}
                      onReply={setReplyingToId}
                      onDelete={handleDelete}
                      onEdit={activeDiscussionId ? handleEdit : undefined}
                      onPreview={setPreviewAttachment}
                      onOpenGallery={openLightbox}
                      isDark={isDark}
                      readStatus={msg.isOwn && msg.wa_status !== "awaiting_consent" ? getReadStatus(msg) : undefined}
                      onReadInfoClick={msg.isOwn ? setReadInfoMsgId : undefined}
                      onRetry={msg.senderType !== "client" && msg.channel === "whatsapp" && msg.wa_status === "failed" ? handleRetry : undefined}
                      createdAt={msg.createdAt}
                      uploadProgress={uploadProgress[msg.id]}
                      isPinned={pinnedIds.has(msg.id)}
                      onPin={handlePin}
                      onUnpin={handleUnpin}
                      onJumpToMessage={jumpToMessage}
                    />
                    </div>
                  );
                })}
              </div>
            </div>
            </Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Typing indicator */}
      {typingNames.length > 0 && (
        <div
          className="flex flex-row items-center"
          style={{ padding: "2px 16px 0", gap: 6 }}
        >
          <TypingDots />
          <span
            className="text-h4"
            style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
          >
            {typingNames.length === 1
              ? `${typingNames[0]} ecrit...`
              : `${typingNames.join(", ")} ecrivent...`}
          </span>
        </div>
      )}

      {/* Input */}
      <div style={{ padding: "12px 16px 16px" }}>
        {waInvite && !clientMuteBlocked && (
          <div
            className="flex flex-row items-center gap-2 flex-wrap"
            style={{
              padding: "8px 12px", marginBottom: 6,
              background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 10,
            }}
          >
            <span className="text-h4 font-medium" style={{ fontFamily: "Poppins", color: "#5B21B6", flex: 1, minWidth: 200 }}>
              {waInvite.sentAt
                ? `SMS envoyé à ${new Date(waInvite.sentAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} — en attente de son message WhatsApp.`
                : "WhatsApp ne livre pas ce client. Invitez-le par SMS à nous écrire sur WhatsApp pour lancer la conversation."}
            </span>
            <button
              onClick={sendWaInviteSms}
              disabled={waInviteBusy}
              className="text-h4 font-semibold rounded-lg transition-colors flex-shrink-0 hover:bg-violet-100 disabled:opacity-40"
              style={{ fontFamily: "Poppins", color: "#5B21B6", padding: "4px 8px" }}
              title="SMS « HericAuto » avec le lien WhatsApp du garage : quand le client écrit, la conversation est ouverte"
            >
              {waInviteBusy ? "Envoi…" : waInvite.sentAt ? "Renvoyer le SMS" : "Envoyer le SMS"}
            </button>
          </div>
        )}
        {/* Client en sourdine — la coupure est volontaire mais invisible autrement :
            rien ne part et le fil ne garde aucune trace de ce qui a été supprimé.
            Le bandeau porte donc la seule action qui la lève. */}
        {clientMuteBlocked && (
          <div
            className="flex flex-row items-center gap-2"
            style={{
              padding: "8px 12px", marginBottom: 6,
              background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 10,
            }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M16.5 8.5a5 5 0 0 1 .5 2.2V13m-2 4H6a2 2 0 0 1-1.6-3.2C5.5 12.4 6 11 6 9.3a6 6 0 0 1 8-5.6" />
              <line x1="3" y1="3" x2="21" y2="21" />
            </svg>
            <span className="text-h4 font-semibold" style={{ fontFamily: "Poppins", color: "#92400E", flex: 1 }}>
              Client en sourdine — aucun email ni message ne lui est envoyé
              {clientMute?.muted_by_name ? ` (coupé par ${clientMute.muted_by_name})` : ""}.
            </span>
            <button
              onClick={() => toggleClientMute(false)}
              disabled={clientMuteBusy}
              className="text-h4 font-semibold rounded-lg transition-colors flex-shrink-0 hover:bg-amber-100"
              style={{ fontFamily: "Poppins", color: "#92400E", padding: "4px 8px" }}
            >
              Réactiver
            </button>
          </div>
        )}
        {/* WhatsApp unavailable — block the send instead of letting it fail silently.
            Without this the message is accepted, fails, and only then shows "Non envoyé".
            The switch being off is deliberate, so it gets a calm amber notice; a dead
            session is a failure, so it gets red and points at the fix. */}
        {waChannelBlocked && (
          <div
            className="flex flex-row items-center gap-2"
            style={{
              padding: "8px 12px",
              marginBottom: 6,
              background: waBlock.reason === "disabled" ? "#FFFBEB" : "#FEF2F2",
              border: `1px solid ${waBlock.reason === "disabled" ? "#FCD34D" : "#FECACA"}`,
              borderRadius: 10,
            }}
          >
            <span
              style={{
                width: 8, height: 8, borderRadius: 999, flexShrink: 0,
                background: waBlock.reason === "disabled" ? "#D97706" : "#DC2626",
              }}
            />
            <span
              className="text-h4 font-semibold"
              style={{ fontFamily: "Poppins", color: waBlock.reason === "disabled" ? "#92400E" : "#991B1B" }}
            >
              {waBlock.reason === "disabled"
                ? "L'envoi WhatsApp est désactivé (Réglages → WhatsApp). Aucun message ne partira tant qu'il est coupé."
                : "WhatsApp est déconnecté — impossible d'envoyer ou de recevoir des messages. Reconnectez la session (bandeau rouge en haut) puis réessayez."}
            </span>
          </div>
        )}
        {waLandline && (
          <div
            className="flex flex-col gap-2"
            style={{ padding: "8px 12px", marginBottom: 6, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10 }}
          >
            <div className="flex flex-row items-center gap-2">
              <span style={{ width: 8, height: 8, borderRadius: 999, flexShrink: 0, background: "#DC2626" }} />
              <span className="text-h4 font-semibold" style={{ fontFamily: "Poppins", color: "#991B1B", flex: 1 }}>
                {storeDiscussion?.whatsapp_phone} est une ligne fixe : WhatsApp ne fonctionnera pas sur ce numéro.{" "}
                {altMobileContacts.length > 0
                  ? "Basculez sur un autre contact du client :"
                  : isEmailLinked ? "Utilisez plutôt l'e-mail." : "Ajoutez un mobile ou un e-mail au client."}
              </span>
              {isEmailLinked && (
                <button
                  onClick={() => setChannelOverride("email")}
                  className="text-h5 font-semibold whitespace-nowrap"
                  style={{ fontFamily: "Poppins", padding: "3px 10px", borderRadius: 6, border: "1px solid #DC2626", color: "#991B1B", background: "#FFFFFF" }}
                >
                  Passer à l'e-mail
                </button>
              )}
            </div>
            {altMobileContacts.length > 0 && (
              <div className="flex flex-row flex-wrap gap-2" style={{ paddingLeft: 16 }}>
                {altMobileContacts.map((alt) => (
                  <button
                    key={alt.id}
                    onClick={() => switchWaToContact(alt)}
                    disabled={switchingWaTo !== null}
                    className="text-h5 font-semibold whitespace-nowrap disabled:opacity-50"
                    style={{ fontFamily: "Poppins", padding: "4px 10px", borderRadius: 6, border: "1px solid #25D366", color: "#166534", background: "#FFFFFF" }}
                  >
                    {switchingWaTo === alt.id ? "Bascule…" : `Basculer sur ${alt.name} (${formatPhoneDisplay(alt.phone)})`}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {replyMessage && (
          <div
            className="flex flex-row items-center justify-between"
            style={{
              padding: "8px 12px",
              marginBottom: 4,
              background: isDark ? "#404040" : "#F5F5F5",
              borderRadius: "10px 10px 0 0",
              borderLeft: `3px solid ${isDark ? "#D4D4D4" : "#525252"}`,
            }}
          >
            <div className="flex flex-col" style={{ gap: 2, overflow: "hidden", flex: 1 }}>
              <span className="text-h3 font-semibold" style={{ fontFamily: "Poppins", color: isDark ? "#D4D4D4" : "#525252" }}>
                {replyMessage.senderName}
              </span>
              <span
                className={isDark ? "text-h4" : "text-h4 text-neutral-400"}
                style={{ fontFamily: "Poppins", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...(isDark ? { color: "#6B7280" } : {}) }}
              >
                {replyMessage.text || "📎 Pièce jointe"}
              </span>
            </div>
            <button
              onClick={() => setReplyingToId(null)}
              className={`flex-shrink-0 ${isDark ? "hover:bg-white/10" : "hover:bg-neutral-200"} rounded-full flex items-center justify-center`}
              style={{ width: 24, height: 24 }}
            >
              <Icon1 name="close" size={10} color="#A1A1A1" />
            </button>
          </div>
        )}

        {/* @mention autocomplete */}
        {mentionQuery !== null && mentionCandidates.length > 0 && (
          <div
            ref={mentionMenuRef}
            className="flex flex-col"
            style={{
              marginBottom: 4,
              background: isDark ? "#2D2D2D" : "#FFFFFF",
              border: `1px solid ${isDark ? "#404040" : "#E5E5E5"}`,
              borderRadius: 10,
              boxShadow: isDark ? "0px 4px 12px rgba(0,0,0,0.4)" : "0px 4px 12px rgba(0,0,0,0.12)",
              overflow: "hidden",
              maxHeight: 220,
              overflowY: "auto",
            }}
          >
            {mentionCandidates.map((m, i) => (
              <button
                key={m.id}
                className="flex flex-row items-center w-full text-left"
                style={{
                  padding: "8px 12px",
                  gap: 10,
                  background: i === mentionIndex ? (isDark ? "rgba(255,255,255,0.08)" : "#F5F5F5") : "transparent",
                }}
                onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
                onMouseEnter={() => setMentionIndex(i)}
              >
                <UserAvatar src={m.photo_url} value={m.avatar_seed || m.name} size={24} className="w-6 h-6 rounded-full" />
                <span
                  className="text-h2"
                  style={{ fontFamily: "Poppins", fontWeight: 500, color: isDark ? "#E5E5E5" : "#1E1E1E" }}
                >
                  {m.name}
                </span>
                {m.role && (
                  <span className="text-h4" style={{ fontFamily: "Poppins", color: "#A1A1A1" }}>
                    {m.role}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Folder ("/") autocomplete */}
        {folderQuery !== null && (
          <FolderSearchPopover
            results={folderResults}
            loading={folderLoading}
            activeIndex={folderIndex}
            onPick={insertFolder}
            onHover={setFolderIndex}
            dark={isDark}
          />
        )}

        {/* Staged attachment preview (email: text + image/file combo) */}
        {stagedFile && (
          <div
            className="flex flex-row items-center"
            style={{
              padding: "8px 12px",
              marginBottom: 4,
              background: isDark ? "#404040" : "#F0F0F0",
              borderRadius: 10,
              gap: 10,
            }}
          >
            {stagedFile.previewUrl ? (
              <img src={stagedFile.previewUrl} alt="" style={{ width: 48, height: 48, borderRadius: 6, objectFit: "cover" }} />
            ) : (
              <Icon1 name="join" size={20} color="#6B7280" />
            )}
            <span className="text-h3 flex-1" style={{ fontFamily: "Poppins", fontWeight: 500, color: isDark ? "#E5E5E5" : "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {stagedFile.file.name}
            </span>
            <button
              onClick={() => setStagedFile(null)}
              className={`flex-shrink-0 ${isDark ? "hover:bg-white/10" : "hover:bg-neutral-200"} rounded-full flex items-center justify-center`}
              style={{ width: 24, height: 24 }}
            >
              <Icon1 name="close" size={10} color="#A1A1A1" />
            </button>
          </div>
        )}

        <div
          className="flex flex-row items-center relative"
          style={{ padding: "8px 10px", gap: 10, background: isDark ? "#404040" : "#F5F5F5", borderRadius: (replyMessage || stagedFile) ? "0 0 10px 10px" : 10 }}
        >
          <div className="relative">
            <button
              onClick={() => setShowAttachMenu(!showAttachMenu)}
              className={`${isDark ? "hover:bg-white/10" : "hover:bg-neutral-200"} rounded-md flex items-center justify-center`}
              style={{ width: 28, height: 28 }}
            >
              <Icon1 name="plus" size={12} color={isDark ? "#D4D4D4" : "#A1A1A1"} />
            </button>
            {showAttachMenu && (
              <div
                ref={attachMenuRef}
                className="absolute z-50 flex flex-col"
                style={{
                  bottom: "100%", left: 0, marginBottom: 8,
                  background: isDark ? "#404040" : "#FFFFFF",
                  boxShadow: isDark ? "0px 4px 12px rgba(0, 0, 0, 0.4)" : "0px 4px 12px rgba(0, 0, 0, 0.15)",
                  borderRadius: 10, overflow: "hidden", minWidth: 160,
                }}
              >
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className={`flex flex-row items-center ${isDark ? "hover:bg-white/10" : "hover:bg-neutral-50"} text-h2`}
                  style={{ padding: "10px 14px", gap: 10, fontFamily: "Poppins", fontWeight: 500, color: isDark ? "#E5E5E5" : "#000000" }}
                >
                  <Icon1 name="image" size={16} color="#A1A1A1" />
                  Image
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={`flex flex-row items-center ${isDark ? "hover:bg-white/10" : "hover:bg-neutral-50"} text-h2`}
                  style={{ padding: "10px 14px", gap: 10, fontFamily: "Poppins", fontWeight: 500, color: isDark ? "#E5E5E5" : "#000000" }}
                >
                  <Icon1 name="join" size={16} color="#A1A1A1" />
                  Fichier
                </button>
                {!(chatTab === "client" && channelTab === "email") && (
                <button
                  onClick={() => { setShowAttachMenu(false); startRecording(); }}
                  className={`flex flex-row items-center ${isDark ? "hover:bg-white/10" : "hover:bg-neutral-50"} text-h2`}
                  style={{ padding: "10px 14px", gap: 10, fontFamily: "Poppins", fontWeight: 500, color: isDark ? "#E5E5E5" : "#000000" }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A1A1A1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="1" width="6" height="12" rx="3" />
                    <path d="M5 10a7 7 0 0 0 14 0" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                  </svg>
                  Audio
                </button>
                )}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileAttach(f, "image"); e.target.value = ""; }}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileAttach(f, "file"); e.target.value = ""; }}
                />
              </div>
            )}
          </div>

          {/* Template picker button — only on WhatsApp channel with Twilio provider */}
          {chatTab === "client" && channelTab === "whatsapp" && isWhatsappLinked && whatsappProvider === "twilio" && (
            <>
              {/* Hors fenêtre 24h le composeur est grisé et le template est la SEULE
                  action possible : on l'affiche alors en bouton plein et libellé,
                  pas en petite icône grise que personne ne repère. */}
              <button
                onClick={() => setShowTemplatePicker(true)}
                className={`${waWindowBlocked && !inputText.trim() ? "hover:opacity-90" : isDark ? "hover:bg-white/10" : "hover:bg-neutral-200"} rounded-md flex items-center justify-center whitespace-nowrap`}
                style={waWindowBlocked && !inputText.trim()
                  ? { height: 28, padding: "0 10px", gap: 6, background: "#25D366", color: "#FFFFFF" }
                  : { width: 28, height: 28 }}
                title={waWindowBlocked ? "Fenêtre de 24h fermée — seul un template approuvé peut partir" : "Envoyer un message pré-rédigé (template WhatsApp)"}
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={waWindowBlocked && !inputText.trim() ? "#FFFFFF" : isDark ? "#D4D4D4" : "#A1A1A1"} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M3 9h18" />
                  <path d="M9 21V9" />
                </svg>
                {waWindowBlocked && !inputText.trim() && <span className="text-h5 font-medium">Envoyer un template</span>}
              </button>
              {showTemplatePicker && activeDiscussionId && (
                <WhatsAppTemplateModal
                  discussionId={activeDiscussionId}
                  windowOpen={whatsappWindowOpen}
                  windowHours={whatsappWindowHours}
                  sending={sendingTemplate}
                  onSend={handleSendTemplate}
                  onClose={() => setShowTemplatePicker(false)}
                />
              )}
            </>
          )}

          {isRecording ? (
            <>
              <button
                onClick={() => stopRecording(false)}
                className={`${isDark ? "hover:bg-white/10" : "hover:bg-neutral-200"} rounded-md flex items-center justify-center`}
                style={{ width: 28, height: 28, flexShrink: 0 }}
                title="Annuler"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 2l10 10M12 2L2 12" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
              <div className="flex-1 flex items-center" style={{ gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#EF4444", animation: "pulse 1.5s ease-in-out infinite" }} />
                <span className="text-h2" style={{ fontFamily: "Poppins", fontWeight: 500, color: isDark ? "#E5E5E5" : "#000000" }}>
                  {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, "0")}
                </span>
              </div>
              <button
                onClick={() => stopRecording(true)}
                className={`${isDark ? "hover:bg-white/10" : "hover:bg-neutral-200"} rounded-md flex items-center justify-center`}
                style={{ width: 28, height: 28, flexShrink: 0 }}
                title="Envoyer"
              >
                <Icon1 name="send" size={16} color={isDark ? "#D4D4D4" : "#525252"} />
              </button>
              <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
            </>
          ) : (
            <>
              <textarea
                ref={inputRef}
                value={inputText}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (mentionQuery !== null && mentionCandidates.length > 0) {
                    if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex((i) => Math.min(i + 1, mentionCandidates.length - 1)); return; }
                    if (e.key === "ArrowUp") { e.preventDefault(); setMentionIndex((i) => Math.max(i - 1, 0)); return; }
                    if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(mentionCandidates[mentionIndex]); return; }
                    if (e.key === "Escape") { e.preventDefault(); setMentionQuery(null); return; }
                  }
                  if (folderQuery !== null && folderResults.length > 0) {
                    if (e.key === "ArrowDown") { e.preventDefault(); setFolderIndex((i) => Math.min(i + 1, folderResults.length - 1)); return; }
                    if (e.key === "ArrowUp") { e.preventDefault(); setFolderIndex((i) => Math.max(i - 1, 0)); return; }
                    if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertFolder(folderResults[folderIndex]); return; }
                    if (e.key === "Escape") { e.preventDefault(); setFolderQuery(null); return; }
                  }
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
                }}
                placeholder={clientMuteBlocked ? "Client en sourdine — réactivez ses notifications pour lui écrire" : waChannelBlocked ? (waBlock.reason === "disabled" ? "Envoi WhatsApp désactivé" : "WhatsApp déconnecté — envoi impossible") : waWindowBlocked ? "Fenêtre 24h fermée — écrivez, le message partira après l'accord du client" : isDark ? "Message interne..." : (chatTab === "client" && channelTab === "whatsapp" && isWhatsappLinked ? "Envoyer via WhatsApp..." : chatTab === "client" && channelTab === "email" && isEmailLinked ? "Envoyer par email..." : "Entrez un message")}
                rows={1}
                disabled={noDiscussion || composerInputBlocked}
                className={`flex-1 outline-none border-none text-h2 resize-none ${isDark ? "placeholder:text-[#6B7280]" : ""}`}
                style={{ fontFamily: "Poppins", fontWeight: 500, color: isDark ? "#FFFFFF" : "#000000", background: "transparent", maxHeight: 120, overflowY: "auto" }}
              />
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                  setComposerPicker({
                    x: Math.min(r.left, window.innerWidth - 360),
                    y: Math.max(8, r.top - 440),
                  });
                }}
                disabled={noDiscussion || composerInputBlocked}
                title="Emoji"
                className={`${isDark ? "hover:bg-white/10" : "hover:bg-neutral-200"} rounded-md flex items-center justify-center`}
                style={{ width: 28, height: 28 }}
              >
                <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6.2" stroke="#A1A1A1" strokeWidth="1.3" />
                  <circle cx="5.9" cy="6.6" r="0.7" fill="#A1A1A1" />
                  <circle cx="10.1" cy="6.6" r="0.7" fill="#A1A1A1" />
                  <path d="M5.4 9.7c.7.9 1.6 1.4 2.6 1.4s1.9-.5 2.6-1.4" stroke="#A1A1A1" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
              </button>
              {waConsentMode ? (
                <button
                  onClick={handleSend}
                  disabled={noDiscussion || sendingConsent || !inputText.trim()}
                  title="Fenêtre de 24 h fermée : le client reçoit une demande d'accord (modèle WhatsApp) et votre message part dès qu'il répond"
                  className="rounded-md flex items-center justify-center whitespace-nowrap hover:opacity-90"
                  style={{ height: 28, padding: "0 10px", flexShrink: 0, background: "#25D366", color: "#FFFFFF", opacity: !inputText.trim() || sendingConsent ? 0.5 : 1 }}
                >
                  <span className="text-h5 font-medium">{sendingConsent ? "Envoi…" : "Envoyer via demande d'accord"}</span>
                </button>
              ) : (
              <button
                onClick={handleSend}
                disabled={noDiscussion || composerBlocked}
                title={clientMuteBlocked ? "Client en sourdine — réactivez ses notifications pour lui écrire" : waChannelBlocked ? (waBlock.reason === "disabled" ? "Envoi WhatsApp désactivé dans les réglages" : "WhatsApp déconnecté — reconnectez la session pour envoyer") : waWindowBlocked ? "Fenêtre de 24h fermée — utilisez un template approuvé" : undefined}
                className={`${isDark ? "hover:bg-white/10" : "hover:bg-neutral-200"} rounded-md flex items-center justify-center`}
                style={{ width: 28, height: 28, opacity: composerBlocked ? 0.4 : 1 }}
              >
                <Icon1 name="send" size={16} color={inputText.trim() ? (isDark ? "#D4D4D4" : "#525252") : "#A1A1A1"} />
              </button>
              )}
            </>
          )}
        </div>

        {composerPicker && createPortal(
          <>
            <div className="fixed inset-0 z-[10001]" onClick={() => setComposerPicker(null)} />
            <div
              style={{ position: "fixed", left: composerPicker.x, top: composerPicker.y, zIndex: 10002 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Picker
                data={emojiData}
                locale="fr"
                theme={isDark ? "dark" : "light"}
                previewPosition="none"
                skinTonePosition="none"
                onEmojiSelect={(emoji: { native: string }) => {
                  insertEmojiAtCaret(emoji.native);
                  setComposerPicker(null);
                }}
              />
            </div>
          </>,
          document.body
        )}
      </div>

      {previewAttachment && (
        <AttachmentPreviewModal
          name={previewAttachment.name}
          url={previewAttachment.url}
          gcsPath={previewAttachment.gcs_path}
          onClose={() => setPreviewAttachment(null)}
          entityType={entityType}
          entityId={entityId}
        />
      )}

      {lightbox && (() => {
        const att = lightbox.list[lightbox.idx];
        if (!att) return null;
        const multi = lightbox.list.length > 1;
        return (
          <AttachmentPreviewModal
            name={att.name || (isImageAtt(att) ? "image.png" : isPdfAtt(att) ? "document.pdf" : "fichier")}
            url={att.base64Data || att.url || ""}
            gcsPath={att.gcs_path}
            onClose={() => setLightbox(null)}
            entityType={entityType}
            entityId={entityId}
            onPrev={multi ? () => lightboxStep(-1) : undefined}
            onNext={multi ? () => lightboxStep(1) : undefined}
            counter={multi ? `${lightbox.idx + 1} / ${lightbox.list.length}` : undefined}
          />
        );
      })()}

      {readInfoMsgId && (() => {
        const msgIndex = messages.findIndex((m) => m.id === readInfoMsgId);
        const activeDisc = useMessagerieStore.getState().discussions.find((d) => d.id === activeDiscussionId);
        return (
          <ReadInfoPanel
            msgIndex={msgIndex}
            messages={messages}
            storeMessages={storeMessages || []}
            readReceipts={readReceipts}
            allMemberIds={activeDisc?.members ?? []}
            storeMembers={storeMembers}
            currentUserId={currentUserId}
            isDark={isDark}
            onClose={() => setReadInfoMsgId(null)}
          />
        );
      })()}
    </div>
    {showInfoPanel && !onToggleInfo && (
      <ChatInfoPanel
        onClose={() => setShowInfoPanel(false)}
        discussionName={entityName || storeDiscussion?.name || "Conversation"}
        discussionDescription={storeDiscussion?.description}
        chatMembers={resolvedMembers}
        messages={messages}
      />
    )}
    </div>
  );
}
